<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { api } from '../lib/api.js';
  import { config } from '../lib/config.js';
  import { 
    appSettingsStore,
    chatsStore,
    chatActivityStore,
    clearDraft,
    classifyChatError,
    connectionStore,
    createChat,
    defaultModelStore,
    deleteChat,
    draftStore,
    generateRequestId,
    getDraft,
    getShowSystemPreference,
    loadModels,
    modelsStore,
    mqttErrorStore,
    playSendSound,
    renameChat,
    serviceModelsStore,
    setCurrentChat,
    setDraft,
    showFlash,
    toggleArchive,
    refreshChatsInBackground,
    markChatReadLocally,
    toggleChatNotifications,
    enableChatNotifications,
    chatNotificationsResultText,
    supportsChatOpen,
    userStore,
    usersStore,
  } from '../lib/stores.js';
  import { beginRegenActivity, isReadByAll, withAssistantMention } from '../lib/chat-state.js';
  import { longpress } from '../lib/actions/longpress.js';
  import { anchorHeight } from '../lib/actions/anchor-height.js';
  import { swipe } from '../lib/actions/swipe.js';
  import { dragTo, navigateWithSlide, pageEntrance, snapBack } from '../lib/page-transition.js';
  import { TEXT, WAITING_FOR_BRAIN, describeStatusMessage } from '../lib/ui-text.js';
  // MQTT грузится ЛЕНИВО (тяжёлый ~372КБ чанк, см. stores.js) — динамический импорт
  // только при отправке/отмене, чтобы не утяжелять initial-бандл и не тормозить старт.
  const loadMqttLib = () => import('../lib/mqtt.js');
  import { downloadFile, exportExchangeToMarkdown, exportToMarkdown, stripSourceLinks } from '../lib/markdown.js';
  import { getTravelPlaceMapsUrl } from '../lib/travel-links.js';
  import {
    appendToDraft,
    buildFinalResponseMessage,
    buildReplyQuote,
    findQuickActionTarget,
    mergeMessageLists,
    prependReplyQuote,
  } from '../lib/chat-messages.js';
  import { messageImages, toDataUrls } from '../lib/message-images.js';
  import { resizeComposer } from '../lib/chat-composer.js';
  import { handOffKeyboard } from '../lib/keyboard-handoff.js';
  import { createArm } from '../lib/arm.js';
  import {
    clearDraftAttachments,
    getDraftAttachments,
    getDraftKey,
    getScrollUpHintSeen,
    setDraftAttachments,
    setScrollUpHintSeen,
  } from '../lib/storage.js';
  import { readSnapshot, writeSnapshot } from '../lib/message-cache.js';
  import {
    deliveryStore,
    flagOrphanFailed,
    flagOrphanPhotoLost,
    forgetDelivery,
    hasPending,
    hasPersistedPending,
    retryNow,
    sendWithDelivery,
  } from '../lib/delivery.js';
  import { compressImageDataUrl } from '../lib/image-compress.js';
  import BrainStatusLine from '../lib/components/BrainStatusLine.svelte';
  import Message from '../lib/components/Message.svelte';
  import StageCard from '../lib/components/StageCard.svelte';
  import MessageTextViewer from '../lib/components/MessageTextViewer.svelte';
  import MessageEditor from '../lib/components/MessageEditor.svelte';
  import BottomSheet from '../lib/components/BottomSheet.svelte';
  import ActionSheet from '../lib/components/ActionSheet.svelte';
  import {
    carryOverDraft,
    collapseSourceLinks,
    dismissStageHint,
    dropStageFromMessages,
    expandSourceLinks,
    foldMessagesForStage,
    removeStage,
    resetInsideStage,
    shouldShowStageHint,
    stageIdOfSummary,
    stageNumber,
    stageTitle,
    upsertStage,
    withStageCards,
  } from '../lib/chat-stages.js';
  import ChatMembersSheet from '../lib/components/ChatMembersSheet.svelte';
  import ModelPickerSheet from '../lib/components/ModelPickerSheet.svelte';
  import PublicationPanel from '../lib/components/PublicationPanel.svelte';
  import { importedByStage, importedMessage, publicationBadge } from '../lib/publication.js';
  import { loadRecentModels, modelLabel, rememberRecentModel } from '../lib/model-picker.js';
  import ListPanel from '../lib/components/ListPanel.svelte';

  export let chatId;
  export let focusMessageId = null;
  // Этап 23: сообщение внутри свёрнутого этапа открывается окном этого этапа.
  export let focusStageId = null;

  let messages = [];
  let inputText = '';
  let loading = true;
  let error = null;
  let chatTitle = '';
  let isArchived = false;
  let hasMore = false;
  let nextBefore = null;
  let loadingMore = false;
  let showSystemMessages = false;
  let currentRequestId = null;
  let currentModel = null; // резолвится из настроек чата/каталога; см. defaultChatModel/sendModel
  let statusMessage = '';
  let initialScrollDone = false;
  let lastPersistedDraft = '';
  let scrollFrame = null;
  let shouldStickToBottom = true;
  // Высота ленты на момент последнего scroll-события или программного скролла:
  // по ней handleScroll отличает жест от подрезки scrollTop после смены высоты.
  let lastScrollHeight = 0;
  let lastScrollTop = 0;
  let suppressStickUpdate = false;
  // Этап 13: окно ленты. 'tail' — обычный режим: последняя страница, подгрузка назад,
  // прилипание к низу. 'history' — окно из начала/середины истории (после удержания
  // «Вверх» или из поиска): хвоста в нём нет, вперёд догружаем по nextAfter, входящие
  // сообщения/стрим в ленту не подмешиваем (встали бы в середину истории с «дырой»),
  // снимок кэша не пишем. «Вниз» из этого режима возвращает хвост.
  let windowMode = 'tail';
  let hasMoreAfter = false;
  let nextAfter = null;
  let loadingNewer = false;
  let jumpingToStart = false;
  let holdingScrollUp = false;
  let scrollUpHintChecked = false;
  let attachmentError = '';
  let selectedImages = [];
  // Правка своего сообщения (этап 22): текст уезжает в композер, а хвост ниже
  // либо удаляется, либо остаётся в ленте вне контекста.
  let editingMessage = null;
  let editKeepTail = false;
  let editError = '';
  let editBusy = false;
  let contextResetAt = null;
  let travelMeta = null;
  let editingPlaceId = null;
  let placeEditTitle = '';
  let placeEditNote = '';
  let placeEditReason = '';
  let placeActionError = '';
  let travelPanelCollapsed = false;
  let scanMeta = null;
  let listMeta = null;
  let editingFragmentId = null;
  let fragmentEditTitle = '';
  let fragmentEditContent = '';
  let fragmentActionError = '';
  let lastLoadedChatId = null;
  let chatLoadSeq = 0;
  let highlightedMessageId = null;
  let lastFocusedMessageId = null;

  // ── Этапы проекта (этап 23) ──
  const STAGE_INCLUDE_LIMIT = 2;
  let stages = [];
  let projectState = null;
  let stageMemory = null;
  let liveCount = null;
  let liveLimit = null;
  // Окно одного этапа (реш. 10): лента показывает только его сообщения, «Вниз» возвращает хвост.
  let stageWindow = null;
  // Чипы «+ Этап N целиком» над композером (реш. 22) — на один запрос.
  let includeStages = [];
  let stageSheetStage = null;
  let stageSheetSummary = null;
  let stageEditOpen = false;
  let stageEditStage = null;
  let stageEditTitle = '';
  let stageEditSummary = '';
  let stageEditOriginal = '';
  let stageEditBusy = false;
  let stageHintTick = 0;
  // Просмотр текста для выделения (1.23.1): { title, text, mode } | null.
  let textViewer = null;
  let highlightTimer = null;
  let snapshotTimer = null;
  let membersSheetOpen = false;
  // Шторка выбора модели из шапки (1.25.7).
  let modelPickerOpen = false;
  let modelPickerBusy = null;
  let modelPickerError = '';
  let recentModels = [];
  // Флажок чата «не отдавать российскому резерву» (этап 25.3) — модели Yandex в шторке неактивны.
  let noDomesticFallback = false;
  // Публикация на сайт (этап 26.4): состояние из /publication (null — недоступна или
  // не владелец), локальная синхронизация и «правка из git» до открытия шторки.
  let publication = null;
  let publicationOpen = false;
  let publicationSyncing = false;
  let publicationImportedFresh = false;
  // Этап 26.5: событие о статье — панель перечитывает её состояние.
  let articleTick = 0;
  // Прочтение (7.3): последняя отправленная на сервер метка и дебаунс.
  let lastMarkedReadTs = null;
  let markReadTimer = null;
  // «Печатает…» (7.3): userId -> момент, до которого показывать; троттлинг своего сигнала.
  let typingUntil = {};
  let typingPruneTimer = null;
  let lastTypingSentAt = 0;
  const TYPING_SEND_INTERVAL_MS = 3000;
  const TYPING_SHOW_MS = 5000;

  // Гистерезис «прилипания» к низу ленты: прилипаем при доскролле вниз в 64px от
  // низа (с инерционным скроллом iOS в старые 2px было не попасть), а отлипаем при
  // любом движении вверх дальше 8px — иначе overscroll-bounce у нижнего края
  // (возврат из-за упругости) считался бы намеренным скроллом вверх.
  const STICK_ATTACH_THRESHOLD_PX = 64;
  const STICK_DETACH_THRESHOLD_PX = 8;
  const KEYBOARD_SCROLL_STEP_PX = 64;
  // Этап 13: тап по «Вверх» листает на 0.9 экрана (как PageUp); кнопка видна, когда есть
  // история для подгрузки или от верха дальше 64px; догрузка вперёд — за 100px до низа окна.
  const PAGE_SCROLL_RATIO = 0.9;
  const SCROLL_UP_FAB_MIN_PX = 64;
  const NEWER_LOAD_THRESHOLD_PX = 100;
  const MOBILE_TRAVEL_PANEL_BREAKPOINT_PX = 640;
  
  let messagesContainer;
  let inputElement;
  let imageInput;

  $: currentActivity = $chatActivityStore[chatId] || null;
  $: currentChatMeta = $chatsStore.find((item) => item.chatId === chatId) || null;
  $: currentStatus = currentActivity;
  $: streamingText = currentActivity?.partialText || '';
  $: activeRequestId = currentActivity?.requestId || currentRequestId;
  $: isGeneratingThisChat = !!(currentActivity && ['received', 'queued', 'processing', 'streaming'].includes(currentActivity.state));
  $: defaultChatModel = $appSettingsStore.defaultModel ?? $defaultModelStore;
  // Модель, реально уходящая в запрос: модель чата, иначе резолвнутый дефолт (с бэка).
  $: sendModel = currentModel ?? defaultChatModel;
  // Подсказка при аттаче картинки. Мультимодальная модель (supportsImages в
  // /api/models) обрабатывает изображение сама — подмены нет и говорить не о чем;
  // для остальных backend переключается на служебную vision-модель, и это показываем.
  $: currentModelSupportsImages = Boolean(
    $modelsStore.find((item) => item.id === sendModel)?.supportsImages
  );
  $: visionModelTitle = !currentModelSupportsImages && $serviceModelsStore?.vision
    ? $serviceModelsStore.vision.split(':').pop()
    : '';
  // Печатать и отправлять можно ВСЕГДА (кроме архива): при генерации запрос встаёт
  // в последовательную очередь бэкенда (FIFO); без сети/MQTT — в офлайн-очередь
  // delivery.js ('queued', уйдёт при восстановлении связи); модель при cold-start
  // офлайне подставит резолвер доставки в момент фактической отправки.
  $: canSendThisChat = !isArchived;
  $: hasPendingContent = Boolean(inputText.trim() || selectedImages.length);
  $: syncedDraftValue = chatId ? ($draftStore[chatId] ?? '') : '';
  $: if (currentChatMeta) {
    chatTitle = currentChatMeta.title || 'Новый чат';
    isArchived = currentChatMeta.isArchived;
  }
  // Семейные чаты: контекст участника из /api/chats (isOwner/isShared/members).
  $: isSharedChat = Boolean(currentChatMeta?.isShared);
  $: isChatOwner = currentChatMeta ? currentChatMeta.isOwner !== false : true;
  $: chatMembers = currentChatMeta?.members || [];
  $: myUserId = $userStore;
  $: chatNotificationsOn = Boolean(currentChatMeta?.notificationsEnabled);
  $: muteAction = {
    id: 'notifications',
    label: chatNotificationsOn ? TEXT.family.disableNotifications : TEXT.family.enableNotifications,
  };

  $: isMentionMode = isSharedChat && currentChatMeta?.responseMode === 'mention';
  // Быстрый вызов Петровича (чип слева от отправки / long-press на отправке):
  // клиент подставляет @Петрович сам, пользователю не нужно переключать раскладку.
  let callAssistant = false;
  $: if (!isMentionMode && callAssistant) {
    callAssistant = false;
  }
  // Последний ts в ленте — граница «прочитано до». Реактивно на любой merge
  // (загрузка, событие участника, ответ Петровича, reconcile).
  $: latestMessageTs = messages.reduce((acc, item) => (item.ts && item.ts > acc ? item.ts : acc), '');
  $: if (chatId && latestMessageTs) {
    scheduleMarkRead(latestMessageTs);
  }
  $: typingNames = Object.keys(typingUntil)
    .filter((userId) => userId !== myUserId)
    .map((userId) => $usersStore[userId]?.displayName || userId);
  $: typingLabel = typingNames.length === 0
    ? ''
    : (typingNames.length === 1
        ? TEXT.family.typingOne.replace('{name}', typingNames[0])
        : TEXT.family.typingMany.replace('{names}', typingNames.join(', ')));
  $: composerPlaceholder = callAssistant
    ? TEXT.family.composerAssistantPlaceholder
    : (isMentionMode ? TEXT.family.composerMentionPlaceholder : 'Введите сообщение...');
  const ASSISTANT_AUTHOR = { name: TEXT.family.assistantName, avatarDataUrl: '/icon-192.png' };

  // Подпись автора в общем чате: чужое user-сообщение — имя и аватарка из ростера,
  // ответ Петровича — его имя и иконка приложения (равноправный участник).
  function foreignAuthorOf(message, rosterMap, selfId, shared) {
    if (message.role === 'assistant') {
      return shared && !message._streaming ? ASSISTANT_AUTHOR : null;
    }
    if (message.role !== 'user' || !message.userId || message.userId === selfId) {
      return null;
    }
    const profile = rosterMap[message.userId];
    return {
      name: profile?.displayName || message.userId,
      avatarDataUrl: profile?.avatarDataUrl || null,
    };
  }

  // Отметить прочитанным до ts: только видимая вкладка, монотонно, с дебаунсом.
  function scheduleMarkRead(ts) {
    if (!ts || (lastMarkedReadTs && ts <= lastMarkedReadTs)) {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    if (markReadTimer) {
      window.clearTimeout(markReadTimer);
    }
    markReadTimer = window.setTimeout(() => {
      markReadTimer = null;
      sendMarkRead(ts);
    }, 400);
  }

  async function sendMarkRead(ts) {
    const targetChatId = chatId;
    if (!targetChatId || !ts || (lastMarkedReadTs && ts <= lastMarkedReadTs)) {
      return;
    }
    lastMarkedReadTs = ts;
    try {
      const result = await api.markChatRead(targetChatId, ts);
      markChatReadLocally(targetChatId, myUserId, result?.lastReadTs || ts, result?.unreadTotal);
    } catch (error) {
      // Сеть: повторим при следующем изменении ленты/видимости.
      if (lastMarkedReadTs === ts) {
        lastMarkedReadTs = null;
      }
      console.warn('Failed to mark chat read', error);
    }
  }

  function noteTyping(userId) {
    if (!userId || userId === myUserId) {
      return;
    }
    typingUntil = { ...typingUntil, [userId]: Date.now() + TYPING_SHOW_MS };
    if (!typingPruneTimer) {
      typingPruneTimer = window.setInterval(() => {
        const now = Date.now();
        const next = Object.fromEntries(Object.entries(typingUntil).filter(([, until]) => until > now));
        if (Object.keys(next).length !== Object.keys(typingUntil).length) {
          typingUntil = next;
        }
        if (!Object.keys(next).length) {
          window.clearInterval(typingPruneTimer);
          typingPruneTimer = null;
        }
      }, 1000);
    }
  }

  function sendTypingSignal() {
    if (!isSharedChat || !chatId) {
      return;
    }
    const now = Date.now();
    if (now - lastTypingSentAt < TYPING_SEND_INTERVAL_MS) {
      return;
    }
    lastTypingSentAt = now;
    loadMqttLib().then((m) => m.publishTyping(chatId)).catch(() => {});
  }

  // «Ответить Петровичу»: подставить @упоминание в композер (reply = упоминание).
  async function insertAssistantMention() {
    const token = TEXT.family.mentionToken;
    if (!inputText.includes(token)) {
      inputText = `${token} ${inputText}`.trimEnd() + (inputText ? '' : ' ');
      setDraft(chatId, inputText);
    }
    await tick();
    inputElement?.focus();
    syncInputHeight(inputElement);
  }
  $: if (chatId) {
    setCurrentChat(chatId);
    // Смена чата: метки прочтения, «печатает…» и чип вызова — состояние прошлого чата.
    lastMarkedReadTs = null;
    typingUntil = {};
    callAssistant = false;
  }
  $: if (
    chatId
    && syncedDraftValue !== lastPersistedDraft
    && syncedDraftValue !== inputText
  ) {
    inputText = syncedDraftValue;
    lastPersistedDraft = syncedDraftValue;
    tick().then(() => syncInputHeight());
  }
  
  // Build display messages including streaming
  // В режиме 'history' стрим не показываем: баббл встал бы под старыми сообщениями.
  $: displayMessages = withStageCards(
    buildDisplayMessages(messages, windowMode === 'history' ? '' : streamingText, activeRequestId),
    stageWindow ? [] : stages,
  );
  $: canManageStages = isChatOwner && !isDachaBrain && !isArchived;
  $: showStageHint = stageHintTick >= 0 && !stageWindow && shouldShowStageHint({
    chatId, liveCount, liveLimit, isOwner: canManageStages, isDacha: isDachaBrain,
  });
  $: stageWindowTitle = stageWindow
    ? TEXT.stages.windowTitle.replace('{title}', stageTitle(stageWindow, stageNumber(stages, stageWindow.stageId), TEXT.stages.cardTitle))
    : '';
  $: stageSheetActions = stageSheetStage
    ? [
        { id: 'open-window', label: TEXT.stages.openMessages, icon: 'list' },
        ...(stageSheetSummary?.text && !stageSheetSummary.summaryHidden
          ? [{ id: 'select-text', label: TEXT.chat.selectTextAction, icon: 'select' }]
          : []),
        ...(stageSheetStage.status === 'ready' && !stageSheetSummary?.summaryHidden
          ? [{ id: 'include', label: TEXT.stages.askWith, icon: 'draft' }]
          : []),
        ...(canManageStages && stageSheetStage.status === 'ready'
          ? [
              { id: 'edit', label: TEXT.stages.editSummary, icon: 'rename' },
              { id: 'regenerate', label: TEXT.stages.regenerate, icon: 'repeat', confirm: TEXT.stages.regenerateConfirm },
              { id: 'regenerate-scratch', label: TEXT.stages.regenerateFromScratch, icon: 'repeat', confirm: TEXT.stages.regenerateFromScratchConfirm },
            ]
          : []),
        ...(canManageStages && stageSheetStage.status === 'failed'
          ? [{ id: 'regenerate', label: TEXT.stages.retry, icon: 'repeat' }]
          : []),
        ...(canManageStages && stageSheetStage.status !== 'pending'
          ? [{ id: 'reopen', label: TEXT.stages.reopen, icon: 'trash', danger: true, confirm: TEXT.stages.reopenConfirm }]
          : []),
      ]
    : [];
  $: showScrollUp = !loading && !error && (hasMore || lastScrollTop > SCROLL_UP_FAB_MIN_PX);
  // Подсказка про удержание — один раз на устройство, при первом появлении кнопки.
  $: if (showScrollUp && !scrollUpHintChecked) {
    scrollUpHintChecked = true;
    if (!getScrollUpHintSeen()) {
      setScrollUpHintSeen();
      showFlash(TEXT.chat.scrollUpHint, 'info', 6000);
    }
  }
  $: lastUserMessage = [...displayMessages].reverse().find((message) => message.role === 'user' && !message._streaming) || null;
  $: contextResetLabel = formatContextReset(contextResetAt);
  $: isTravelChat = (currentChatMeta?.chatType === 'travel')
    || travelMeta?.chatType === 'travel'
    || Boolean(travelMeta?.places?.length);
  $: isScanChat = (currentChatMeta?.chatType === 'scan') || scanMeta?.chatType === 'scan';
  $: isListChat = (currentChatMeta?.chatType === 'list') || listMeta?.chatType === 'list';
  // Этап 10: чат отвечает дачным Петровичем (поле brain) или это служебный чат «Дача».
  $: isDachaBrain = currentChatMeta?.brain === 'dacha' || currentChatMeta?.chatType === 'dacha';
  // Модель меняет только владелец; у дачи своего выбора нет, архив только читается.
  $: canPickModel = isChatOwner && !isDachaBrain && !isArchived;
  $: publicationChip = publicationBadge(publication, { syncing: publicationSyncing, importedFresh: publicationImportedFresh });
  $: gitImportedByStage = importedByStage(publication);

  // Свайпы навигации (этап 10): влево — экран контекста чата, вправо — список чатов.
  // Экран идёт за пальцем (dragTo), при зачёте уезжает и следующий въезжает с нужной
  // стороны (navigateWithSlide), без зачёта — возвращается пружинкой (snapBack).
  let pageEl = null;

  function openContextScreen() {
    return navigateWithSlide(pageEl, 'left', `/context/${chatId}`);
  }

  function goToChatList() {
    return navigateWithSlide(pageEl, 'right', '/chats');
  }

  function onSwipeMove(event) {
    dragTo(pageEl, event.detail.dx);
  }

  function onSwipeEnd(event) {
    if (!event.detail.committed) {
      snapBack(pageEl);
    }
  }
  $: scanFragments = scanMeta?.fragments || [];
  $: yandexLinksInDraft = extractYandexMapsLinks(inputText);
  $: explicitPlacesInDraft = extractExplicitPlaceNames(inputText);
  // ── Статусы генерации (В9, 1.27.8) ──
  // Раньше «получено», «в очереди» и «обработка» сливались в одно «Отправлено»: при
  // долгом ожидании было непонятно, стоит ли запрос за предыдущим ответом или модель
  // думает. Счётчик секунд — с входа запроса в текущее состояние на этом устройстве.
  const THINKING_COUNTER_FROM_S = 3;
  let phaseMark = { requestId: null, state: null, since: 0 };
  let statusNow = Date.now();
  let statusTicker = null;

  function trackStatusPhase(status) {
    const requestId = status?.requestId || null;
    const state = status?.state || null;
    if (requestId !== phaseMark.requestId || state !== phaseMark.state) {
      phaseMark = { requestId, state, since: Date.now() };
      statusNow = phaseMark.since;
    }
    const ticking = state === 'processing';
    if (ticking && !statusTicker) {
      statusTicker = setInterval(() => {
        statusNow = Date.now();
      }, 1000);
    } else if (!ticking && statusTicker) {
      clearInterval(statusTicker);
      statusTicker = null;
    }
  }

  $: trackStatusPhase(currentStatus);
  $: statusSeconds = Math.max(0, Math.floor((statusNow - phaseMark.since) / 1000));

  // Итог отмены и ошибки показывает строка статуса (с «Повторить» у ошибки, 1.27.0);
  // отдельная строка statusMessage с тем же текстом его только дублировала.
  $: if (['cancelled', 'error', 'idle'].includes(currentActivity?.state)) {
    currentRequestId = null;
  }

  // ── Быстрые действия и ответ заново (1.27.0) ──
  // Под последним сообщением хвоста ленты: ответ — «Копировать · Ещё раз ·
  // Поделиться», вопрос без ответа — «Повторить». В окне истории и этапа хвоста нет.
  $: quickTarget = windowMode === 'tail' && !stageWindow ? findQuickActionTarget(displayMessages) : null;
  $: canShareText = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  // Ответить заново можно в личном чате, когда Петрович не пишет: в общем ответ
  // видят все, и backend тоже откажет.
  $: canRegenerate = Boolean(quickTarget?.message?.requestId)
    && !String(quickTarget.message.requestId).startsWith('stage:')
    && !isSharedChat && !isArchived && !isGeneratingThisChat;
  // Ошибка без ответа: «Повторить» живёт в строке статуса, рядом с текстом ошибки.
  $: statusRetryRequestId = canRegenerate
    && quickTarget.kind === 'question'
    && currentStatus?.chatId === chatId
    && currentStatus.state === 'error'
    && currentStatus.requestId === quickTarget.message.requestId
    ? currentStatus.requestId
    : null;

  function quickActionsFor(message, target, regenerable, shareable, statusRetryId, delivery, pendingRequestId) {
    if (!target || target.message.id !== message.id) {
      return null;
    }
    if (target.kind === 'answer') {
      return ['copy', ...(regenerable ? ['regenerate'] : []), ...(shareable ? ['share'] : [])];
    }
    // Вопрос без ответа: повтор, когда запрос дошёл и не в работе. Недоставленный
    // повторяет своя кнопка «Повторить отправку».
    const requestId = message.requestId;
    if (!regenerable || !requestId || statusRetryId === requestId || pendingRequestId === requestId) {
      return null;
    }
    const deliveryState = delivery?.[requestId];
    if (deliveryState && deliveryState !== 'sent') {
      return null;
    }
    return ['retry'];
  }

  // Тот же вопрос отвечается заново командой regen: прежний ответ backend удалит
  // сам и разошлёт message_deleted, новый ляжет под тот же вопрос. Активность
  // начинаем до публикации — иначе отказ backend (статус error того же requestId)
  // отсеялся бы как запоздавший итог прошлой попытки.
  // Сразу после открытия приложения MQTT ещё поднимается, а команда управления,
  // в отличие от сообщения, в очередь доставки не встаёт. Ждём соединение
  // недолго — тап «Ещё раз» в первые секунды не должен кончаться отказом.
  const REGEN_CONNECT_WAIT_MS = 10000;

  function waitForMqttConnection(timeoutMs = REGEN_CONNECT_WAIT_MS) {
    return new Promise((resolve) => {
      let done = false;
      let unsubscribe = null;
      const finish = (connected) => {
        if (done) {
          return;
        }
        done = true;
        clearTimeout(timer);
        unsubscribe?.();
        resolve(connected);
      };
      const timer = setTimeout(() => finish(false), timeoutMs);
      unsubscribe = connectionStore.subscribe((state) => {
        if (state === 'connected') {
          finish(true);
        }
      });
      if (done) {
        unsubscribe();
      }
    });
  }

  async function regenerateAnswer(requestId) {
    if (!requestId || !chatId || isGeneratingThisChat) {
      return;
    }
    const targetChatId = chatId;
    const previousActivity = $chatActivityStore[targetChatId] || null;
    chatActivityStore.update((current) => beginRegenActivity(current, targetChatId, requestId, sendModel));
    currentRequestId = requestId;
    try {
      const mqtt = await loadMqttLib();
      if (!(await waitForMqttConnection())) {
        throw new Error('MQTT is not connected');
      }
      await mqtt.publishControl(requestId, targetChatId, 'regen', sendModel);
    } catch (e) {
      chatActivityStore.update((current) => {
        const activity = current[targetChatId];
        if (activity?.requestId !== requestId || activity.state !== 'received') {
          return current;
        }
        const { [targetChatId]: _dropped, ...rest } = current;
        return previousActivity ? { ...rest, [targetChatId]: previousActivity } : rest;
      });
      if (currentRequestId === requestId) {
        currentRequestId = null;
      }
      showFlash(TEXT.chat.regenerateFailed, 'error');
    }
  }

  async function shareMessageText(message) {
    if (!message?.text) {
      return;
    }
    try {
      await navigator.share({ text: message.text });
    } catch (e) {
      // Человек закрыл системное меню — это не ошибка.
      if (e?.name === 'AbortError') {
        return;
      }
      await copyMessageText(message);
    }
  }

  // «Ответить»: цитата сообщения встаёт в начало черновика, набранное остаётся ниже.
  // Фокус — синхронно, пока идёт тап: иначе iOS не откроет клавиатуру.
  async function quoteIntoDraft(message) {
    const hasImages = messageImages(message).length > 0;
    const quote = buildReplyQuote(message?.text, { imageLabel: hasImages ? TEXT.chat.replyImageQuote : '' });
    if (!quote || !chatId) {
      return;
    }
    inputText = prependReplyQuote(inputText, quote);
    setDraft(chatId, inputText);
    lastPersistedDraft = inputText;
    inputElement?.focus();
    await tick();
    syncInputHeight(inputElement);
    // Шторка при закрытии возвращает фокус туда, где он был до неё, — забираем обратно.
    inputElement?.focus();
    inputElement?.setSelectionRange?.(inputText.length, inputText.length);
  }

  // Клавиатура iOS открылась/закрылась (viewport.js) — лента ужалась; если были
  // прижаты к низу, доскролливаем, чтобы последнее сообщение осталось видимым.
  function handleViewportResized() {
    if (shouldStickToBottom) {
      scheduleScrollToBottom();
    }
  }

  // Разделители дат в ленте: «Сегодня» / «Вчера» / «5 августа».
  function messageDayKey(message) {
    if (!message?.ts || message._streaming) {
      return null;
    }
    const date = new Date(message.ts);
    return Number.isNaN(date.getTime()) ? null : date.toDateString();
  }

  function dateBoundaryBefore(message, index) {
    const dayKey = messageDayKey(message);
    if (!dayKey) {
      return false;
    }
    if (index === 0) {
      return true;
    }
    return messageDayKey(displayMessages[index - 1]) !== dayKey;
  }

  function formatDateLabel(isoString) {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return 'Сегодня';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    }
    const options = { day: 'numeric', month: 'long' };
    if (date.getFullYear() !== today.getFullYear()) {
      options.year = 'numeric';
    }
    return date.toLocaleDateString('ru-RU', options);
  }

  // Стартовые подсказки пустого чата: тап кладёт текст в композер (не отправляет).
  const EMPTY_CHAT_SUGGESTIONS = [
    'Составь план на выходные',
    'Проверь текст на ошибки',
    'Объясни простыми словами…',
  ];

  // Тап по пустому месту ленты снимает фокус с композера: на iOS клавиатура
  // иначе висит, пока не нажмёшь «галочку» на её панели. Тапы по бабблу, ссылкам
  // и кнопкам сюда не доходят — у них своя обработка.
  function handleMessagesAreaClick(event) {
    if (event.target.closest('.message-bubble, a, button, input, textarea, label')) {
      return;
    }
    if (document.activeElement === inputElement) {
      inputElement.blur();
    }
  }

  async function applySuggestion(text) {
    inputText = text.endsWith('…') ? `${text.slice(0, -1)} ` : text;
    if (chatId) {
      setDraft(chatId, inputText);
      lastPersistedDraft = inputText;
    }
    await tick();
    syncInputHeight();
    inputElement?.focus();
  }

  function formatContextReset(value) {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Контекст сброшен';
    }

    return `Контекст сброшен ${date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  function travelProfileRows(profile = {}) {
    const rows = [];
    if (profile.city) rows.push(['Город', profile.city]);
    if (profile.dates) rows.push(['Даты', profile.dates]);
    if (profile.interests?.length) rows.push(['Интересы', profile.interests.join(', ')]);
    if (profile.pace) rows.push(['Темп', profile.pace]);
    if (profile.transport) rows.push(['Транспорт', profile.transport]);
    if (profile.budget) rows.push(['Бюджет', profile.budget]);
    return rows;
  }

  // Свёрнутая панель показывает только название и кнопку: профиль поездки на
  // телефоне занимал полэкрана над лентой (замечание владельца 2026-09-12).
  $: travelProfileRowsList = travelProfileRows(travelMeta?.profile);
  $: hasTravelDetails = Boolean(travelMeta?.places?.length) || travelProfileRowsList.length > 0;

  function placeStatusLabel(status) {
    return {
      enriched: 'проверено',
      partial: 'частично',
      pending: 'ожидает проверки',
      failed: 'не найдено',
      disabled: 'без API',
    }[status] || 'ожидает проверки';
  }

  function placeCoordinates(place) {
    if (typeof place?.latitude !== 'number' || typeof place?.longitude !== 'number') {
      return '';
    }
    return `${place.latitude.toFixed(6)}, ${place.longitude.toFixed(6)}`;
  }

  function openPlaceEditor(place) {
    editingPlaceId = place.placeId;
    placeEditTitle = place.title || '';
    placeEditNote = place.note || '';
    placeEditReason = place.selectionReason || '';
    placeActionError = '';
  }

  function closePlaceEditor() {
    editingPlaceId = null;
    placeEditTitle = '';
    placeEditNote = '';
    placeEditReason = '';
    placeActionError = '';
  }

  function getDefaultTravelPanelCollapsed() {
    return typeof window !== 'undefined' && window.innerWidth <= MOBILE_TRAVEL_PANEL_BREAKPOINT_PX;
  }

  async function toggleTravelPanel() {
    travelPanelCollapsed = !travelPanelCollapsed;
    if (travelPanelCollapsed) {
      closePlaceEditor();
    }
    await tick();
    if (shouldStickToBottom) {
      scheduleScrollToBottom();
    }
  }

  async function savePlace(place) {
    if (!chatId || !place?.placeId) {
      return;
    }
    try {
      const updated = await api.updateTravelPlace(chatId, place.placeId, {
        title: placeEditTitle,
        note: placeEditNote || null,
        selectionReason: placeEditReason || null,
      });
      travelMeta = {
        ...travelMeta,
        places: (travelMeta?.places || []).map((item) => item.placeId === updated.placeId ? updated : item),
      };
      closePlaceEditor();
    } catch (e) {
      placeActionError = e?.message || 'Не удалось сохранить место.';
    }
  }

  // В13 (1.27.8): «Удалить» у места и фрагмента стоит вплотную к «Отмена» и раньше
  // срабатывало сразу — теперь вторым касанием.
  const { armed: rowDeleteArmed, tap: tapRowDelete } = createArm();

  async function removePlace(place) {
    if (!chatId || !place?.placeId) {
      return;
    }
    try {
      await api.deleteTravelPlace(chatId, place.placeId);
      travelMeta = {
        ...travelMeta,
        places: (travelMeta?.places || []).filter((item) => item.placeId !== place.placeId),
      };
      closePlaceEditor();
    } catch (e) {
      placeActionError = e?.message || 'Не удалось удалить место.';
    }
  }

  function openFragmentEditor(fragment) {
    editingFragmentId = fragment.fragmentId;
    fragmentEditTitle = fragment.title || '';
    fragmentEditContent = fragment.content || '';
    fragmentActionError = '';
  }

  function closeFragmentEditor() {
    editingFragmentId = null;
    fragmentEditTitle = '';
    fragmentEditContent = '';
    fragmentActionError = '';
  }

  async function saveFragment(fragment) {
    if (!chatId || !fragment?.fragmentId) {
      return;
    }
    try {
      const updated = await api.updateScanFragment(chatId, fragment.fragmentId, {
        title: fragmentEditTitle || null,
        content: fragmentEditContent,
      });
      scanMeta = {
        ...scanMeta,
        fragments: (scanMeta?.fragments || []).map((item) => item.fragmentId === updated.fragmentId ? updated : item),
      };
      closeFragmentEditor();
    } catch (e) {
      fragmentActionError = e?.message || 'Не удалось сохранить фрагмент.';
    }
  }

  async function removeFragment(fragment) {
    if (!chatId || !fragment?.fragmentId) {
      return;
    }
    try {
      await api.deleteScanFragment(chatId, fragment.fragmentId);
      scanMeta = {
        ...scanMeta,
        fragments: (scanMeta?.fragments || []).filter((item) => item.fragmentId !== fragment.fragmentId),
      };
      closeFragmentEditor();
    } catch (e) {
      fragmentActionError = e?.message || 'Не удалось удалить фрагмент.';
    }
  }

  function syncAttachmentDraft() {
    if (!chatId) {
      return;
    }
    if (selectedImages.length) {
      const persisted = setDraftAttachments(chatId, selectedImages);
      if (!persisted) {
        attachmentError = 'Изображения не сохранятся в черновике из-за нехватки памяти браузера, но их можно отправить.';
      }
    } else {
      clearDraftAttachments(chatId);
    }
  }

  function buildDisplayMessages(msgs, streaming, reqId) {
    const result = [...msgs];
    
    if (streaming && reqId) {
      result.push({
        id: `streaming_${reqId}`,
        role: 'assistant',
        text: streaming,
        ts: null,
        requestId: reqId,
        model: defaultChatModel,
        _streaming: true,
      });
    }
    
    return result;
  }

  function upsertFinalResponse(payload) {
    if (!payload?.requestId || windowMode === 'history') {
      return;
    }

    messages = mergeMessageLists(messages, [buildFinalResponseMessage(payload, {
      streamingText,
      currentModel,
      defaultModel: defaultChatModel,
    })]);
  }

  // Сверка ленты с сервером после ответа, resume и реконнекта. До 1.24.2 каждый раз
  // тянулась вся первая страница — для чата с фото это мегабайты base64 на каждый
  // ответ. Теперь берём только новее последнего известного серверного сообщения
  // (курсор — его id, граница строгая); если курсор сервер не знает (сообщение
  // удалили) или известных нет — полная страница, как раньше. Параллельные вызовы
  // (response + status + resume) сливаются в один запрос.
  let reconcileInFlight = null;

  function reconcileLatestMessages(targetChatId = chatId, loadSeq = chatLoadSeq) {
    if (windowMode === 'history') {
      // Хвост подмешаем, когда окно доедет до него (loadNewerMessages) или по «Вниз».
      return Promise.resolve();
    }
    if (reconcileInFlight) {
      return reconcileInFlight;
    }
    reconcileInFlight = runReconcile(targetChatId, loadSeq).finally(() => {
      reconcileInFlight = null;
    });
    return reconcileInFlight;
  }

  // Курсор сверки — последнее сообщение, про которое известно, что оно с сервера,
  // и которое стоит РАНЬШЕ любого оптимистичного (local_ вопрос, response_ ответ):
  // именно их сверка и должна заменить серверными копиями с настоящими id — без
  // этого у свежего обмена нет пунктов «Изменить»/«Удалить» и этап его не сворачивает.
  function isOptimisticId(id) {
    return typeof id !== 'string' || id.startsWith('local_') || id.startsWith('response_');
  }

  function lastServerMessageCursor() {
    let cursor = null;
    for (const message of messages) {
      if (isOptimisticId(message?.id)) {
        break;
      }
      if (message.ts) {
        cursor = message.id.startsWith('msg_') ? message.id : message.ts;
      }
    }
    return cursor;
  }

  async function runReconcile(targetChatId, loadSeq) {
    const cursor = lastServerMessageCursor();
    try {
      if (cursor) {
        try {
          const page = await api.getMessagesAfter(targetChatId, cursor, config.messagesPageSize, { stage: null });
          if (isStaleChatLoad(targetChatId, loadSeq)) {
            return;
          }
          messages = mergeMessageLists(messages, page.items || []);
          return;
        } catch (e) {
          // 400 Unknown cursor: последнее известное сообщение удалено — полная страница ниже.
          if (e?.status !== 400) {
            throw e;
          }
        }
      }
      const result = await api.getMessages(targetChatId, config.messagesPageSize, null, { stage: null });
      // Чат мог смениться пока шёл запрос — не мержим ответ старого чата в новый.
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      messages = mergeMessageLists(messages, result.items);
    } catch (e) {
      console.warn('Failed to reconcile messages after response', e);
    }
  }

  // Тихо (без спиннера) обновляет модель/сброс-контекста/travel-мету чата.
  // silent=false — пробрасывает ошибку (используется внутри loadMessages);
  // silent=true — глушит (используется на resume, не должен ронять экран).
  async function refreshChatMetaQuietly(targetChatId, loadSeq, { silent = true } = {}) {
    try {
      if (supportsChatOpen()) {
        // Этап 30: настройки, мета по типу, этапы и публикация — одним запросом.
        const opened = await api.openChat(targetChatId, { messages: false });
        if (!isStaleChatLoad(targetChatId, loadSeq)) {
          await applyOpenedChatMeta(opened, targetChatId, loadSeq);
        }
        return;
      }
      const settings = await api.getChatSettings(targetChatId);
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      currentModel = settings.model || defaultChatModel;
      noDomesticFallback = Boolean(settings.noDomesticFallback);
      contextResetAt = settings.contextResetAt || null;
      showSystemMessages = getShowSystemPreference(targetChatId);
      await loadChatTypeMeta(targetChatId, loadSeq, settings.chatType);
      await loadStages(targetChatId, loadSeq);
      await loadPublication(targetChatId, loadSeq);
    } catch (e) {
      if (!silent) {
        throw e;
      }
      console.warn('Failed to refresh chat settings on resume', e);
    }
  }

  // Этап 30: мета из /open — то, что раньше несли по очереди настройки, мета по типу,
  // этапы и публикация. Мета приходит только своего типа, остальные обнуляются.
  async function applyOpenedChatMeta(opened, targetChatId, loadSeq) {
    const settings = opened.settings || {};
    currentModel = settings.model || defaultChatModel;
    noDomesticFallback = Boolean(settings.noDomesticFallback);
    contextResetAt = settings.contextResetAt || null;
    showSystemMessages = getShowSystemPreference(targetChatId);
    scanMeta = opened.scan ?? null;
    listMeta = opened.listSnapshot ?? null;
    stages = Array.isArray(opened.stages?.stages) ? opened.stages.stages : [];
    projectState = opened.stages?.state ?? null;
    stageMemory = opened.stages?.memory ?? null;
    publication = isChatOwner && opened.publication?.available ? opened.publication : null;
    const keepBottom = shouldStickToBottom;
    const hadTravel = Boolean(travelMeta);
    travelMeta = opened.travel ?? null;
    // Панель поездки меняет высоту ленты — держим низ, как при отдельной загрузке меты.
    if (travelMeta && !hadTravel && keepBottom) {
      await tick();
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        scheduleScrollToBottom();
      }
    }
    // Коммиты публикации /open не несёт (за ними ходят в Forgejo) — только у
    // опубликованного чата догружаем полный ответ фоном.
    if (publication?.published && !isStaleChatLoad(targetChatId, loadSeq)) {
      loadPublication(targetChatId, loadSeq);
    }
  }

  // Мета по типу чата — только нужная: путевому чату места и профиль, скан-чату
  // фрагменты, списку пункты. Тип берём из настроек чата (он актуален: чат может
  // стать путевым по ходу разговора, а список чатов у клиента отстаёт). Без типа в
  // ответе (старый backend) — как раньше, все три запроса.
  async function loadChatTypeMeta(targetChatId, loadSeq, chatType) {
    if (!chatType) {
      await loadTravelMeta(targetChatId, loadSeq);
      await loadScanMeta(targetChatId, loadSeq);
      await loadListMeta(targetChatId, loadSeq);
      return;
    }
    if (chatType === 'travel') {
      await loadTravelMeta(targetChatId, loadSeq);
    } else if (chatType === 'scan') {
      await loadScanMeta(targetChatId, loadSeq);
    } else if (chatType === 'list') {
      await loadListMeta(targetChatId, loadSeq);
    }
  }

  function isStaleChatLoad(targetChatId, loadSeq) {
    return targetChatId !== chatId || loadSeq !== chatLoadSeq;
  }

  async function loadMessages(refresh = false, { keepBottom = false, targetChatId = chatId, loadSeq = chatLoadSeq, silent = false, ignoreFocus = false } = {}) {
    // silent=true — «тихий» refresh после гидрации из кэша: не показываем спиннер и не
    // стираем уже отрисованные сообщения, а домержаем свежую первую страницу поверх.
    // ignoreFocus=true — возврат к хвосту из окна истории: focusMessageId ещё стоит в
    // адресе, но заново грузить окно вокруг него не нужно.
    if (refresh && !silent) {
      loading = true;
      messages = [];
      nextBefore = null;
      hasMore = false;
    }
    
    // Объявлено до try: флаг читается и в finally.
    let shouldLoadAroundFocus = false;
    try {
      shouldLoadAroundFocus = Boolean(refresh && focusMessageId && !ignoreFocus && targetChatId === chatId);
      const focusStage = shouldLoadAroundFocus && focusStageId ? focusStageId : null;
      // Этап 30: обычное открытие (хвост ленты, без окна этапа и перехода к найденному)
      // — история и вся мета чата одним запросом вместо пяти по очереди.
      const opened = refresh && !shouldLoadAroundFocus && !stageWindow && supportsChatOpen()
        ? await api.openChat(targetChatId, { limit: config.messagesPageSize })
        : null;
      const result = opened
        ? opened.messages
        : shouldLoadAroundFocus
          ? await api.getMessagesAround(targetChatId, focusMessageId, config.messagesPageSize, { stage: focusStage })
          : await api.getMessages(targetChatId, config.messagesPageSize, silent ? null : nextBefore, { stage: stageWindow?.stageId || null });
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }

      if (focusStage) {
        // Этап 23 (реш. 10): цель внутри свёрнутого этапа — лента становится окном этапа.
        stageWindow = stages.find((item) => item.stageId === focusStage) || { stageId: focusStage, title: null, status: 'ready' };
      }
      if (refresh) {
        // silent: неразрушающий merge поверх гидрированных из кэша сообщений.
        messages = silent ? mergeMessageLists(messages, result.items) : result.items;
        // Окно вокруг найденного сообщения не доходит до хвоста — лента в режиме 'history'.
        applyForwardCursor(shouldLoadAroundFocus ? result : null);
      } else {
        messages = [...result.items, ...messages];
      }
      if (!stageWindow) {
        rememberLiveCount(result);
      }
      
      hasMore = result.hasMore;
      nextBefore = result.nextBefore;
      
      let chat = currentChatMeta || (opened ? { title: opened.title, isArchived: opened.isArchived } : null);
      if (!chat) {
        const chats = await api.listChats(false);
        const archivedChats = await api.listChats(true);
        if (isStaleChatLoad(targetChatId, loadSeq)) {
          return;
        }
        const allChats = [...chats.items, ...archivedChats.items];
        chat = allChats.find((item) => item.chatId === targetChatId);
      }
      
      if (chat) {
        chatTitle = chat.title || 'Новый чат';
        isArchived = chat.isArchived;
      } else if (refresh) {
        error = 'Чат удалён или недоступен.';
        window.location.hash = '/chats';
        return;
      }

      if (opened) {
        await applyOpenedChatMeta(opened, targetChatId, loadSeq);
      } else {
        await refreshChatMetaQuietly(targetChatId, loadSeq, { silent });
      }
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }

      error = null;
    } catch (e) {
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      if (silent) {
        // Контент уже показан из кэша — сетевую ошибку (в т.ч. офлайн) не эскалируем
        // в экран ошибки, остаёмся на закэшированной версии.
        console.warn('Silent refresh failed, keeping cached messages', e);
        return;
      }
      if (shouldLoadAroundFocus && focusStageId && e?.status === 404) {
        // Источник удалён (реш. 21): открываем весь этап без подсветки.
        loading = false;
        showFlash(TEXT.stages.sourceMissing, 'info');
        await openStageWindow({ stageId: focusStageId, title: null, status: 'ready' });
        return;
      }
      error = classifyChatError(e, 'Не удалось загрузить чат.');
      if (error.includes('Чат удалён')) {
        window.location.hash = '/chats';
      }
    } finally {
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      loading = false;
      if (keepBottom) {
        shouldStickToBottom = true;
        initialScrollDone = true;
        tick().then(scheduleScrollToBottom);
      } else if (shouldLoadAroundFocus) {
        shouldStickToBottom = false;
        initialScrollDone = true;
        tick().then(() => focusSearchMessage(focusMessageId));
      }
    }
  }

  async function loadMoreMessages() {
    if (loadingMore || !hasMore) return;
    
    loadingMore = true;
    try {
      const previousHeight = messagesContainer?.scrollHeight || 0;
      const previousTop = messagesContainer?.scrollTop || 0;
      const result = await api.getMessages(chatId, config.messagesPageSize, nextBefore, { stage: stageWindow?.stageId || null });
      messages = [...result.items, ...messages];
      hasMore = result.hasMore;
      nextBefore = result.nextBefore;
      await tick();
      if (messagesContainer) {
        suppressStickUpdate = true;
        messagesContainer.scrollTop = messagesContainer.scrollHeight - previousHeight + previousTop;
        lastScrollTop = messagesContainer.scrollTop;
        queueMicrotask(() => {
          suppressStickUpdate = false;
        });
      }
    } finally {
      loadingMore = false;
    }
  }

  async function activateChat(nextChatId) {
    if (!nextChatId) {
      return;
    }

    const loadSeq = ++chatLoadSeq;
    lastLoadedChatId = nextChatId;
    error = null;
    chatTitle = '';
    isArchived = false;
    hasMore = false;
    nextBefore = null;
    loadingMore = false;
    windowMode = 'tail';
    hasMoreAfter = false;
    nextAfter = null;
    loadingNewer = false;
    jumpingToStart = false;
    holdingScrollUp = false;
    currentRequestId = null;
    statusMessage = '';
    currentModel = defaultChatModel;
    noDomesticFallback = false;
    modelPickerOpen = false;
    publication = null;
    publicationOpen = false;
    publicationSyncing = false;
    publicationImportedFresh = false;
    contextResetAt = null;
    travelMeta = null;
    scanMeta = null;
    listMeta = null;
    travelPanelCollapsed = getDefaultTravelPanelCollapsed();
    closePlaceEditor();
    highlightedMessageId = null;
    lastFocusedMessageId = null;
    attachmentError = '';
    initialScrollDone = false;
    shouldStickToBottom = true;
    stages = [];
    projectState = null;
    stageMemory = null;
    liveCount = null;
    liveLimit = null;
    stageWindow = null;
    includeStages = [];
    stageSheetStage = null;
    stageEditOpen = false;
    textViewer = null;

    // Мгновенная гидрация из кэша: если есть снимок и это не переход к найденному в
    // поиске сообщению (там нужен around-load), показываем закэшированный хвост сразу
    // и БЕЗ спиннера — сеть затем тихо (silent) домержит свежую версию. Иначе обычная
    // загрузка со спиннером.
    const cached = focusMessageId ? [] : readSnapshot(nextChatId);
    const canHydrate = cached.length > 0;
    if (canHydrate) {
      messages = cached;
      loading = false;
      // Сироты: оптимистичные сообщения из снапшота, чья доставка не пережила reload
      // (например, офлайн-очередь с картинками — картинки не персистятся). Помечаем
      // failed → «Не доставлено» + «Повторить» (повтор отправит текст заново).
      for (const cachedMessage of cached) {
        if (
          cachedMessage.role === 'user'
          && typeof cachedMessage.id === 'string'
          && cachedMessage.id.startsWith('local_')
          && cachedMessage.requestId
          && !hasPending(cachedMessage.requestId)
          && !hasPersistedPending(cachedMessage.requestId)
        ) {
          // К5 (1.27.7): с фото повторять нечего — «Фото не сохранилось · Прикрепить заново».
          if (cachedMessage.imageCount) {
            flagOrphanPhotoLost(cachedMessage.requestId);
          } else {
            flagOrphanFailed(cachedMessage.requestId);
          }
        }
      }
    } else {
      messages = [];
      loading = true;
    }

    inputText = getDraft(nextChatId) || '';
    lastPersistedDraft = inputText;
    selectedImages = getDraftAttachments(nextChatId);
    editingMessage = null;
    editKeepTail = false;
    editError = '';
    showSystemMessages = getShowSystemPreference(nextChatId);

    await tick();
    if (isStaleChatLoad(nextChatId, loadSeq)) {
      return;
    }
    syncInputHeight();
    await loadMessages(true, { keepBottom: !focusMessageId, targetChatId: nextChatId, loadSeq, silent: canHydrate });
  }

  function handleScroll(e) {
    const { scrollTop, scrollHeight } = e.target;
    // Высота ленты изменилась с прошлого события: сообщения выше экрана сжались
    // (content-visibility после программного скролла в низ) или пришёл ответ —
    // браузер подрезает scrollTop, и такое «движение вверх» жестом не является.
    const contentResized = scrollHeight !== lastScrollHeight;
    // scrollTop === lastScrollTop: браузер дублирует scroll-событие после
    // программного скролла — позиция не менялась, прилипание не трогаем.
    if (!suppressStickUpdate && scrollTop !== lastScrollTop) {
      const isMovingUp = scrollTop < lastScrollTop;
      const distanceFromBottom = getDistanceFromBottom();
      if (isMovingUp) {
        if (contentResized && shouldStickToBottom) {
          // Не жест — лента «дышит»: остаёмся прилипшими и доезжаем к новому низу.
          scheduleScrollToBottom();
        } else if (distanceFromBottom > STICK_DETACH_THRESHOLD_PX) {
          shouldStickToBottom = false;
        }
      } else if (!shouldStickToBottom && windowMode !== 'history') {
        // В окне истории низ — не хвост чата: прилипать не к чему, «Вниз» остаётся.
        shouldStickToBottom = distanceFromBottom <= STICK_ATTACH_THRESHOLD_PX;
      }
    }
    if (!shouldStickToBottom && scrollFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
    }
    lastScrollTop = scrollTop;
    lastScrollHeight = scrollHeight;
    if (scrollTop < 100 && hasMore && !loadingMore) {
      loadMoreMessages();
    }
    if (windowMode === 'history' && hasMoreAfter && !loadingNewer && getDistanceFromBottom() < NEWER_LOAD_THRESHOLD_PX) {
      loadNewerMessages();
    }
  }

  async function sendPreparedMessage({ text = '', images = [], fromComposer = false, allowWhileGenerating = false } = {}) {
    const normalizedText = text.trim();
    // Отправка при идущей генерации легальна для любых чатов: кадры/вопросы встают в
    // backend-очередь (FIFO), не дожидаясь предыдущего ответа (allowWhileGenerating
    // оставлен в сигнатуре для совместимости вызовов скан-чата).
    if ((!normalizedText && !images.length) || !canSendThisChat) {
      return false;
    }

    // Этап 31: картинки из истории приходят ссылками, а в запросе backend ждёт data URL.
    let imageDataUrls;
    try {
      imageDataUrls = await toDataUrls(images.map((item) => item.dataUrl));
    } catch (error) {
      console.warn('Failed to prepare images for sending', error);
      showFlash(TEXT.chat.imageFetchFailed, 'error');
      return false;
    }

    const requestId = generateRequestId();
    currentRequestId = requestId;

    const optimisticUserMessage = {
      id: `local_${requestId}`,
      role: 'user',
      text: normalizedText,
      imageDataUrls,
      ts: new Date().toISOString(),
      requestId,
      model: sendModel,
    };

    if (fromComposer) {
      inputText = '';
      selectedImages = [];
      attachmentError = '';
      clearDraft(chatId);
      clearDraftAttachments(chatId);
      lastPersistedDraft = '';
      if (imageInput) {
        imageInput.value = '';
      }
      // Клавиатура iOS не прячется сама: после отправки она закрывала бы ответ,
      // ради которого сообщение и писали (жалоба владельца 2026-09-12).
      inputElement?.blur();
      await tick();
      syncInputHeight();
    }

    if (windowMode === 'history') {
      // Пишем из середины истории — сначала возвращаем хвост, иначе новое сообщение
      // встало бы в окно без хвоста.
      const targetChatId = chatId;
      await returnToTail();
      if (chatId !== targetChatId) {
        return false;
      }
    }

    messages = [...messages, optimisticUserMessage];
    await tick();
    shouldStickToBottom = true;
    scheduleScrollToBottom();

    try {
      // Доставка с подтверждением (delivery.js): PUBACK + ожидание ACK бэкенда,
      // тихие ре-publish при флапе; статус на сообщении из deliveryStore.
      // Синхронный throw — только при заведомом офлайне браузера (rollback ниже).
      const includeIds = fromComposer ? includeStages.map((item) => item.stageId) : [];
      sendWithDelivery({ requestId, chatId, model: sendModel, text: normalizedText, imageDataUrls, includeStages: includeIds });
      if (fromComposer) {
        includeStages = [];
      }
      playSendSound();
      statusMessage = '';
      return true;
    } catch (e) {
      console.error('Failed to send message:', e);
      messages = messages.filter((message) => message.id !== optimisticUserMessage.id);
      currentRequestId = null;
      statusMessage = e?.message || 'Сообщение не отправилось. Попробуйте ещё раз.';

      if (fromComposer) {
        inputText = normalizedText;
        selectedImages = images;
        setDraft(chatId, normalizedText);
        setDraftAttachments(chatId, images);
        lastPersistedDraft = normalizedText;
        await tick();
        syncInputHeight();
      }

      return false;
    }
  }

  async function sendMessage() {
    await sendComposer(callAssistant);
  }

  // Long-press на кнопке отправки: отправить Петровичу (в режиме «по упоминанию»).
  async function sendToAssistant() {
    await sendComposer(true);
  }

  async function sendComposer(mentionAssistant) {
    const shouldMention = Boolean(mentionAssistant) && isMentionMode;
    const text = shouldMention ? withAssistantMention(inputText, TEXT.family.mentionToken) : inputText;
    const sent = await sendPreparedMessage({
      text,
      images: selectedImages,
      fromComposer: true,
    });
    if (sent) {
      // Чип одноразовый: следующее сообщение — обычное, чтобы не звать случайно.
      callAssistant = false;
    }
  }

  function toggleCallAssistant() {
    callAssistant = !callAssistant;
  }

  function handleCancel() {
    if (activeRequestId && isGeneratingThisChat) {
      loadMqttLib()
        .then((m) => m.publishControl(activeRequestId, chatId, 'cancel', sendModel))
        .catch((e) => {
          statusMessage = e?.message || 'Не удалось отправить отмену: нет соединения.';
        });
    }
  }

  function handleInput(e) {
    const nextValue = e.currentTarget.value;
    syncInputHeight(e.currentTarget);
    if (chatId && nextValue !== lastPersistedDraft) {
      setDraft(chatId, nextValue);
      lastPersistedDraft = nextValue;
      if (nextValue.trim()) {
        sendTypingSignal();
      }
    }
  }

  function extractYandexMapsLinks(value) {
    const matches = value.match(/https?:\/\/(?:[\w.-]+\.)?yandex\.[\w.-]+\/maps[^\s<>()"']*/gi) || [];
    return [...new Set(matches.map((item) => item.replace(/[.,;:!?)\]}]+$/, '')))];
  }

  function extractExplicitPlaceNames(value) {
    const pattern = /(?:^|[\n.!?]\s*)(?:(?:добавь|сохрани|запомни)\s+(?:место|точку(?:\s+интереса)?|достопримечательность)\s*[:—-]?\s*|(?:место|точка\s+интереса|достопримечательность)\s*[:—-]\s*)([^\n.!?;]+)/gi;
    const result = [];
    let match;
    while ((match = pattern.exec(value || ''))) {
      const title = match[1].replace(/https?:\/\/\S+/g, '').replace(/\s+/g, ' ').trim();
      if (title) {
        result.push(title);
      }
    }
    return [...new Set(result)];
  }

  // Запрос меты путевого чата один на чат в полёте (1.24.3): при открытии путевого
  // чата её просили и реактивная догрузка по chatType, и обычный путь через
  // настройки — в прод-логе два /travel подряд.
  let travelMetaInFlight = null;

  function loadTravelMeta(targetChatId = chatId, loadSeq = chatLoadSeq) {
    if (travelMetaInFlight?.chatId === targetChatId) {
      return travelMetaInFlight.promise;
    }
    const promise = runLoadTravelMeta(targetChatId, loadSeq).finally(() => {
      if (travelMetaInFlight?.promise === promise) {
        travelMetaInFlight = null;
      }
    });
    travelMetaInFlight = { chatId: targetChatId, promise };
    return promise;
  }

  async function runLoadTravelMeta(targetChatId, loadSeq) {
    const keepBottom = shouldStickToBottom;
    try {
      const result = await api.getTravelChat(targetChatId);
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      travelMeta = result;
      await tick();
      if (keepBottom && !isStaleChatLoad(targetChatId, loadSeq)) {
        scheduleScrollToBottom();
      }
    } catch (e) {
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        travelMeta = null;
      }
    }
  }

  async function loadListMeta(targetChatId = chatId, loadSeq = chatLoadSeq) {
    try {
      const result = await api.getChatList(targetChatId);
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      listMeta = result;
    } catch (e) {
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        listMeta = null;
      }
    }
  }

  async function loadScanMeta(targetChatId = chatId, loadSeq = chatLoadSeq) {
    try {
      const result = await api.getScanChat(targetChatId);
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      scanMeta = result;
    } catch (e) {
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        scanMeta = null;
      }
    }
  }

  function syncInputHeight(textarea = inputElement) {
    resizeComposer(textarea);
  }

  async function readFileAsDataUrl(file) {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('Не удалось прочитать файл.'));
      reader.readAsDataURL(file);
    });
  }

  async function handleImageChange(event) {
    const files = Array.from(event.currentTarget.files || []);
    if (!files.length) {
      return;
    }
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    if (!imageFiles.length) {
      attachmentError = 'Можно загрузить только изображения.';
      if (imageInput) {
        imageInput.value = '';
      }
      return;
    }
    const remaining = config.maxImagesPerMessage - selectedImages.length;
    if (remaining <= 0) {
      attachmentError = `Можно прикрепить не более ${config.maxImagesPerMessage} изображений.`;
      if (imageInput) {
        imageInput.value = '';
      }
      return;
    }
    try {
      attachmentError = '';
      const accepted = imageFiles.slice(0, remaining);
      const added = [];
      for (const file of accepted) {
        const rawDataUrl = await readFileAsDataUrl(file);
        const dataUrl = await compressImageDataUrl(rawDataUrl);
        added.push({ dataUrl, name: file.name });
      }
      selectedImages = [...selectedImages, ...added];
      if (imageFiles.length > remaining) {
        attachmentError = `Добавлены не все: лимит ${config.maxImagesPerMessage} изображений.`;
      }
      syncAttachmentDraft();
    } catch (e) {
      attachmentError = e.message || 'Не удалось загрузить изображение.';
    } finally {
      if (imageInput) {
        imageInput.value = '';
      }
    }
  }

  async function handleCameraCapture(event) {
    // Скан-чат: каждое фото с камеры сразу отправляется на распознавание (авто-захват),
    // кадры встают в очередь — можно фотографировать подряд, не дожидаясь генерации.
    const input = event.currentTarget;
    const files = Array.from(input?.files || []);
    if (input) {
      input.value = '';
    }
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    if (!imageFiles.length) {
      return;
    }
    for (const file of imageFiles) {
      try {
        const rawDataUrl = await readFileAsDataUrl(file);
        const dataUrl = await compressImageDataUrl(rawDataUrl);
        await sendPreparedMessage({
          text: '',
          images: [{ dataUrl, name: file.name }],
          fromComposer: false,
          allowWhileGenerating: true,
        });
      } catch (e) {
        attachmentError = e.message || 'Не удалось обработать фото.';
      }
    }
  }

  async function analyzeScanBlock() {
    await sendPreparedMessage({
      text: 'Проанализируй собранный документ и сделай выводы.',
      images: [],
      fromComposer: false,
    });
  }

  function clearSelectedImages() {
    selectedImages = [];
    attachmentError = '';
    clearDraftAttachments(chatId);
    if (imageInput) {
      imageInput.value = '';
    }
  }

  function removeImageAt(index) {
    selectedImages = selectedImages.filter((_, i) => i !== index);
    attachmentError = '';
    syncAttachmentDraft();
    if (imageInput) {
      imageInput.value = '';
    }
  }

  async function restoreComposer(message, { targetChatId = chatId } = {}) {
    const messageText = message?.text || '';
    const sourceImages = messageImages(message);
    const restoredImages = sourceImages.map((dataUrl, index) => ({
      dataUrl,
      name: `Восстановленное изображение ${index + 1}`,
    }));

    if (!targetChatId) {
      return;
    }

    // К6 (1.27.2): в открытом чате набранное не затирается — текст сообщения
    // дописывается ниже, картинки добавляются к прикреплённым в пределах лимита.
    // В новый чат («В новый чат») переносится как есть: его черновик пуст.
    const sameChat = targetChatId === chatId;
    const nextText = sameChat ? appendToDraft(inputText, messageText) : messageText;
    const combinedImages = sameChat ? [...selectedImages, ...restoredImages] : restoredImages;
    const nextImages = combinedImages.slice(0, config.maxImagesPerMessage);
    const imagesTruncated = combinedImages.length > nextImages.length;

    setDraft(targetChatId, nextText);
    let persistedAttachment = true;
    if (nextImages.length) {
      persistedAttachment = setDraftAttachments(targetChatId, nextImages);
    } else {
      clearDraftAttachments(targetChatId);
    }

    if (sameChat) {
      inputText = nextText;
      selectedImages = nextImages;
      lastPersistedDraft = nextText;
      attachmentError = !persistedAttachment
        ? 'Изображения не сохранятся в черновике из-за нехватки памяти браузера, но их можно отправить.'
        : (imagesTruncated ? `Добавлены не все: лимит ${config.maxImagesPerMessage} изображений.` : '');
      await tick();
      syncInputHeight();
    }
  }

  async function createNewChatFromMessage(message) {
    try {
      const newChatId = await createChat();
      await restoreComposer(message, { targetChatId: newChatId });
      window.location.hash = `/chat/${newChatId}`;
      showFlash('Запрос перенесён в новый чат.', 'info');
    } catch (e) {
      statusMessage = classifyChatError(e, 'Не удалось создать новый чат.');
    }
  }

  // Просмотр для выделения (1.23.1). Перед открытием снимаем фокус с поля ввода: иначе
  // --app-height остаётся «клавиатурной», и полноэкранный просмотр обрезается.
  function openTextViewer(title, text, mode) {
    if (typeof document !== 'undefined' && typeof document.activeElement?.blur === 'function') {
      document.activeElement.blur();
    }
    textViewer = { title, text: text || '', mode };
  }

  async function handleTextViewerCopy() {
    if (!textViewer) {
      return;
    }
    await copyMessageText({ text: stripSourceLinks(textViewer.text) });
  }

  async function copyMessageText(message) {
    if (!message?.text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(message.text);
      showFlash(TEXT.chat.copied, 'info');
    } catch (_) {
      // Нет API буфера обмена или браузер отказал — без отклика тап выглядел бы мёртвым.
      showFlash(TEXT.chat.copyFailed, 'error');
    }
  }

  function findExchangeUserMessage(message) {
    const targetIndex = messages.findIndex((item) => item.id === message.id);
    if (targetIndex <= 0) {
      return lastUserMessage;
    }

    for (let index = targetIndex - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'user') {
        return messages[index];
      }
    }

    return null;
  }

  function downloadExchange(message) {
    const pairedUserMessage = findExchangeUserMessage(message);
    const content = exportExchangeToMarkdown(chatTitle, pairedUserMessage, message);
    const safeName = chatTitle ? chatTitle.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_').slice(0, 40) : `chat-${chatId.slice(0, 8)}`;
    downloadFile(content, `${safeName}-exchange.md`);
  }

  function contextBoundaryBefore(message, index) {
    if (!contextResetAt || !message?.ts) {
      return false;
    }
    // Этап 23: сброс, попавший внутрь свёрнутого этапа, в ленте не показывается.
    if (resetInsideStage(stages, contextResetAt)) {
      return false;
    }

    const messageTs = Date.parse(message.ts);
    const resetTs = Date.parse(contextResetAt);
    if (Number.isNaN(messageTs) || Number.isNaN(resetTs) || messageTs < resetTs) {
      return false;
    }

    if (index === 0) {
      return true;
    }

    const previousTs = Date.parse(displayMessages[index - 1]?.ts || 0);
    return Number.isNaN(previousTs) || previousTs < resetTs;
  }

  async function handleMessageAction(event) {
    const { type, message } = event.detail || {};
    if (!type || !message) {
      return;
    }

    if (type === 'open-actions') {
      actionSheetMessage = message;
      return;
    }

    if (type === 'copy') {
      await copyMessageText(message);
      return;
    }

    if (type === 'select-text') {
      openTextViewer(
        messagePreviewForSheet(message),
        message.text,
        message.role === 'assistant' ? 'markdown' : 'plain',
      );
      return;
    }

    if (type === 'reuse' || type === 'restore') {
      await restoreComposer(message);
      return;
    }

    if (type === 'reply-assistant') {
      await insertAssistantMention();
      return;
    }

    if (type === 'reply') {
      await quoteIntoDraft(message);
      return;
    }

    if (type === 'regenerate' || type === 'retry') {
      await regenerateAnswer(message.requestId);
      return;
    }

    if (type === 'share') {
      await shareMessageText(message);
      return;
    }

    if (type === 'delete') {
      await deleteMessageExchange(message);
      return;
    }

    if (type === 'delete-above') {
      await deleteMessagesAbove(message);
      return;
    }

    if (type === 'delete-below') {
      await deleteMessagesBelow(message);
      return;
    }

    if (type === 'repeat') {
      const repeatSource = messageImages(message);
      await sendPreparedMessage({
        text: message.text,
        images: repeatSource.map((dataUrl, i) => ({ dataUrl, name: `Повторное изображение ${i + 1}` })),
        fromComposer: false,
      });
      return;
    }

    if (type === 'reattach') {
      // К5 (1.27.7): выбор фото — синхронно, пока идёт тап (иначе iOS его не откроет);
      // текст сообщения — в поле ввода, недоставленный пузырь уходит.
      imageInput?.click();
      inputText = appendToDraft(inputText, message.text || '');
      if (chatId) {
        setDraft(chatId, inputText);
        lastPersistedDraft = inputText;
      }
      messages = messages.filter((item) => item.id !== message.id);
      forgetDelivery(message.requestId);
      persistMessagesSnapshot();
      await tick();
      syncInputHeight(inputElement);
      return;
    }

    if (type === 'retry-delivery') {
      if (!retryNow(message.requestId)) {
        // Сирота (записи доставки нет после reload): отправляем текст заново новым
        // requestId и убираем старый недоставленный баббл.
        const orphanImages = messageImages(message).map((dataUrl, i) => ({ dataUrl, name: `Изображение ${i + 1}` }));
        const resent = await sendPreparedMessage({
          text: message.text,
          images: orphanImages,
          fromComposer: false,
        });
        if (resent) {
          messages = messages.filter((item) => item.id !== message.id);
        }
      }
      return;
    }

    if (type === 'new-chat') {
      await createNewChatFromMessage(message);
      return;
    }

    if (type === 'export-exchange') {
      downloadExchange(message);
      return;
    }

    if (type === 'edit') {
      startEditMessage(message);
      return;
    }

    if (type === 'print-exchange') {
      // Печатный вид сам сведёт обмен по requestId — сюда достаточно якоря.
      window.location.hash = `/print/${chatId}?message=${encodeURIComponent(message.id)}`;
    }
  }

  // ── Этапы проекта (этап 23) ──

  function rememberLiveCount(result) {
    if (typeof result?.liveCount === 'number') {
      liveCount = result.liveCount;
      liveLimit = typeof result.liveLimit === 'number' ? result.liveLimit : liveLimit;
    }
  }

  async function loadStages(targetChatId = chatId, loadSeq = chatLoadSeq) {
    try {
      const data = await api.getStages(targetChatId);
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      stages = Array.isArray(data?.stages) ? data.stages : [];
      projectState = data?.state ?? null;
      stageMemory = data?.memory ?? null;
    } catch (e) {
      // Прежний backend ручки не знает — лента живёт без карточек.
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        console.warn('Failed to load stages', e);
      }
    }
  }

  function stageForMessage(message) {
    const stageId = stageIdOfSummary(message);
    return stages.find((item) => item.stageId === stageId) || {
      stageId,
      title: null,
      status: 'ready',
      messageCount: 0,
      fromTs: message.ts,
      toTs: message.ts,
      carryOver: [],
    };
  }

  function stageErrorText(e, fallback) {
    const detail = typeof e?.detail === 'string' ? e.detail : (e?.message || '');
    if (e?.status === 409) {
      if (/дачном/i.test(detail)) return TEXT.stages.closeDacha;
      if (/нечего/i.test(detail)) return TEXT.stages.closeNothing;
      if (/пишутся|пишется/i.test(detail)) return TEXT.stages.closePending;
      if (/generation/i.test(detail)) return TEXT.stages.closeBusy;
    }
    return classifyChatError(e, fallback);
  }

  async function reloadTailQuietly() {
    if (windowMode === 'history' || !chatId) {
      return;
    }
    await loadMessages(true, {
      keepBottom: shouldStickToBottom,
      targetChatId: chatId,
      loadSeq: chatLoadSeq,
      silent: true,
    });
  }

  function applyStageEvent(payload) {
    const stage = payload.stage || null;
    if (payload.memory) {
      stageMemory = payload.memory;
    }
    if (payload.action === 'closed' && stage) {
      stages = upsertStage(stages, stage);
      if (windowMode !== 'history') {
        messages = foldMessagesForStage(messages, stage);
        liveCount = 0;
      }
      persistMessagesSnapshot();
      return;
    }
    if (payload.action === 'ready' && stage) {
      stages = upsertStage(stages, stage);
      if (payload.summary && windowMode !== 'history') {
        messages = mergeMessageLists(messages, [payload.summary]);
      }
      if ('state' in payload) {
        projectState = payload.state;
      }
      persistMessagesSnapshot();
      return;
    }
    if (payload.action === 'failed' && stage) {
      stages = upsertStage(stages, stage);
      return;
    }
    if (payload.action === 'updated' && stage) {
      stages = upsertStage(stages, stage);
      if (payload.summary) {
        messages = messages.map((item) => (item.id === payload.summary.id ? payload.summary : item));
      }
      persistMessagesSnapshot();
      return;
    }
    if (payload.action === 'reopened' && stage) {
      stages = removeStage(stages, stage.stageId);
      messages = dropStageFromMessages(messages, stage.stageId);
      includeStages = includeStages.filter((item) => item.stageId !== stage.stageId);
      if (stageWindow?.stageId === stage.stageId) {
        returnToTail();
      } else {
        reloadTailQuietly();
      }
      return;
    }
    if (payload.action === 'state') {
      projectState = 'state' in payload ? payload.state : projectState;
    }
  }

  async function closeStage(title = null) {
    try {
      const result = await api.closeStage(chatId, title);
      if (result?.stage) {
        stages = upsertStage(stages, result.stage);
        if (windowMode !== 'history') {
          messages = foldMessagesForStage(messages, result.stage);
        }
        liveCount = 0;
        persistMessagesSnapshot();
      }
      showFlash(TEXT.stages.closeQueued, 'info');
      return true;
    } catch (e) {
      showFlash(stageErrorText(e, TEXT.stages.closeFailed), 'error');
      return false;
    }
  }

  // ── Публикация на сайт (этап 26.4) ──
  // Состояние спрашиваем только в своём чате: у других людей backend всё равно
  // ответит available=false, а лишний запрос на каждом открытии ни к чему.
  async function loadPublication(targetChatId = chatId, loadSeq = chatLoadSeq) {
    if (!isChatOwner) {
      publication = null;
      return;
    }
    try {
      const data = await api.getPublication(targetChatId);
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      publication = data?.available ? data : null;
    } catch (e) {
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        publication = null;
      }
    }
  }

  function openPublication() {
    publicationOpen = true;
    publicationImportedFresh = false;
    loadPublication();
  }

  function handlePublicationChanged(event) {
    const next = event.detail.publication;
    const wasPublished = Boolean(publication?.published);
    publication = next;
    if (!wasPublished && next?.published) {
      showFlash(next.status === 'error' ? TEXT.publish.failed : TEXT.publish.published, next.status === 'error' ? 'error' : 'info');
    } else if (wasPublished && !next?.published) {
      showFlash(TEXT.publish.unpublished, 'info');
      publicationOpen = false;
    }
  }

  // «Закрыть этап и опубликовать»: конспект допишется фоном и сам появится на сайте
  // (автопубликация новых этапов), публикацию не ждём.
  async function closeStageAndPublish(event) {
    const options = event.detail;
    publicationSyncing = true;
    try {
      if (await closeStage(null)) {
        handlePublicationChanged({ detail: { publication: await api.publishChat(chatId, options) } });
      }
    } catch (e) {
      showFlash(e?.message || TEXT.publish.failed, 'error');
    } finally {
      publicationSyncing = false;
    }
  }

  function applyPublicationEvent(payload) {
    if (payload.article) {
      // Статья собралась или упала (этап 26.5): панель перечитает её, тост — если шторка закрыта.
      articleTick += 1;
      if (!publicationOpen) {
        if (payload.article === 'ready') {
          showFlash(TEXT.publish.article.readyToast, 'info', 6000);
        } else if (payload.article === 'failed') {
          showFlash(TEXT.publish.article.failedToast, 'error');
        }
      }
      return;
    }
    if (publication) {
      publication = { ...publication, status: payload.status, publishedAt: payload.publishedAt, error: payload.error };
    }
    const imported = Array.isArray(payload.imported) ? payload.imported : [];
    if (imported.length) {
      imported.forEach((item) => showFlash(importedMessage(item, stages), 'info', 6000));
      if (!publicationOpen) {
        publicationImportedFresh = true;
      }
    }
    if (imported.length || payload.status === 'conflict') {
      loadPublication();
    }
  }

  function dismissHint() {
    dismissStageHint(chatId, liveCount ?? 0);
    stageHintTick += 1;
  }

  async function openStageWindow(stage, { focusId = null } = {}) {
    const targetChatId = chatId;
    const loadSeq = ++chatLoadSeq;
    stageWindow = stage;
    shouldStickToBottom = false;
    try {
      const result = focusId
        ? await api.getMessagesAround(targetChatId, focusId, config.messagesPageSize, { stage: stage.stageId })
        : await api.getMessagesFromStart(targetChatId, config.messagesPageSize, { stage: stage.stageId });
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      messages = result.items;
      hasMore = Boolean(result.hasMore);
      nextBefore = result.nextBefore || null;
      applyForwardCursor(result);
      windowMode = 'history';
      await tick();
      if (focusId) {
        focusSearchMessage(focusId);
      } else {
        scrollToTopOfWindow();
      }
    } catch (e) {
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      if (focusId && e?.status === 404) {
        showFlash(TEXT.stages.sourceMissing, 'info');
        await openStageWindow(stage);
        return;
      }
      stageWindow = null;
      windowMode = 'tail';
      showFlash(classifyChatError(e, TEXT.stages.windowOpenFailed), 'error');
    }
  }

  function addIncludeStage(stage) {
    if (includeStages.some((item) => item.stageId === stage.stageId)) {
      return;
    }
    if (includeStages.length >= STAGE_INCLUDE_LIMIT) {
      showFlash(TEXT.stages.includeLimit.replace('{count}', String(STAGE_INCLUDE_LIMIT)), 'info');
      return;
    }
    includeStages = [...includeStages, stage];
    inputElement?.focus();
  }

  function removeIncludeStage(stageId) {
    includeStages = includeStages.filter((item) => item.stageId !== stageId);
  }

  function openStageEditor(stage, summary) {
    stageEditStage = stage;
    stageEditTitle = stage.title || '';
    stageEditOriginal = summary?.text || '';
    stageEditSummary = collapseSourceLinks(stageEditOriginal);
    stageEditOpen = true;
  }

  async function saveStageEdit() {
    if (!stageEditStage || stageEditBusy) {
      return;
    }
    stageEditBusy = true;
    try {
      const fields = {};
      const nextTitle = stageEditTitle.trim();
      if (nextTitle !== (stageEditStage.title || '')) {
        fields.title = nextTitle;
      }
      const expanded = expandSourceLinks(stageEditSummary.trim(), stageEditOriginal);
      if (expanded && expanded !== stageEditOriginal) {
        fields.summary = expanded;
      }
      if (Object.keys(fields).length) {
        const result = await api.updateStage(chatId, stageEditStage.stageId, fields);
        if (result?.stage) {
          stages = upsertStage(stages, result.stage);
          if (fields.summary && result.stage.summaryMessageId) {
            messages = messages.map((item) => (item.id === result.stage.summaryMessageId ? { ...item, text: expanded } : item));
          }
        }
        showFlash(TEXT.stages.editSaved, 'info');
      }
      stageEditOpen = false;
    } catch (e) {
      showFlash(stageErrorText(e, TEXT.stages.editFailed), 'error');
    } finally {
      stageEditBusy = false;
    }
  }

  async function handleStageAction(event) {
    const { type, stage, summary, messageId } = event.detail || {};
    if (!type || !stage) {
      return;
    }
    if (type === 'open-actions') {
      stageSheetStage = stage;
      stageSheetSummary = summary || null;
      return;
    }
    if (type === 'open-source' && messageId) {
      window.location.hash = `/chat/${chatId}?messageId=${encodeURIComponent(messageId)}&stage=${encodeURIComponent(stage.stageId)}`;
      return;
    }
    if (type === 'open-window') {
      await openStageWindow(stage);
      return;
    }
    if (type === 'select-text') {
      if (summary?.text) {
        openTextViewer(stageTitle(stage, stageNumber(stages, stage.stageId), TEXT.stages.cardTitle), summary.text, 'markdown');
      }
      return;
    }
    if (type === 'include') {
      addIncludeStage(stage);
      return;
    }
    if (type === 'carry-over') {
      // К6 (1.27.2): хвосты этапа дописываются к набранному и сохраняются в черновик.
      inputText = appendToDraft(inputText, carryOverDraft(stage, stageNumber(stages, stage.stageId), TEXT.stages.carryOverPrefix));
      if (chatId) {
        setDraft(chatId, inputText);
        lastPersistedDraft = inputText;
      }
      await tick();
      syncInputHeight();
      inputElement?.focus();
      return;
    }
    if (type === 'edit') {
      openStageEditor(stage, summary);
      return;
    }
    if (type === 'regenerate' || type === 'regenerate-scratch') {
      try {
        const result = await api.regenerateStage(chatId, stage.stageId, type === 'regenerate-scratch');
        if (result?.stage) {
          stages = upsertStage(stages, result.stage);
        }
        showFlash(TEXT.stages.regenerateQueued, 'info');
      } catch (e) {
        showFlash(stageErrorText(e, TEXT.stages.actionFailed), 'error');
      }
      return;
    }
    if (type === 'reopen') {
      try {
        await api.reopenStage(chatId, stage.stageId);
        stages = removeStage(stages, stage.stageId);
        messages = dropStageFromMessages(messages, stage.stageId);
        includeStages = includeStages.filter((item) => item.stageId !== stage.stageId);
        if (stageWindow?.stageId === stage.stageId) {
          await returnToTail();
        } else {
          await reloadTailQuietly();
        }
        showFlash(TEXT.stages.reopened, 'info');
      } catch (e) {
        showFlash(stageErrorText(e, TEXT.stages.actionFailed), 'error');
      }
    }
  }

  async function handleStageSheetSelect(event) {
    const stage = stageSheetStage;
    const summary = stageSheetSummary;
    stageSheetStage = null;
    stageSheetSummary = null;
    if (!stage) {
      return;
    }
    await handleStageAction({ detail: { type: event.detail.id, stage, summary } });
  }

  // ── Шторка действий сообщения (тап/long-press по бабблу) ──
  let actionSheetMessage = null;
  let chatMenuOpen = false;

  function messagePreviewForSheet(message) {
    const text = (message?.text || '').replace(/\s+/g, ' ').trim();
    const prefix = message?.role === 'user' ? 'Вы: ' : 'Ответ: ';
    const body = text || (messageImages(message).length ? 'Изображение' : '');
    return body ? `${prefix}${body.slice(0, 80)}` : '';
  }

  // Удаление (этап 15): пункты появляются, только когда действие реально доступно —
  // свой обмен или владелец чата, вне идущей генерации.
  $: deleteSheetActions = [
    ...(canDeleteMessage(actionSheetMessage, messages, isChatOwner, myUserId, isGeneratingThisChat, activeRequestId, isArchived)
      ? [{
          id: 'delete',
          label: TEXT.chat.deleteAction,
          icon: 'trash',
          danger: true,
          confirm: TEXT.chat.deleteConfirm,
        }]
      : []),
    ...(canClearAbove(actionSheetMessage, messages, isChatOwner, hasMore, isGeneratingThisChat, isArchived)
      ? [{
          id: 'delete-above',
          label: TEXT.chat.deleteAboveAction,
          icon: 'trash',
          danger: true,
          confirm: TEXT.chat.deleteAboveConfirm,
        }]
      : []),
  ];

  $: editTailCount = editingMessage ? tailSize(editingMessage, messages) : 0;

  // Поджатое меню (1.23.3): частое — на первом уровне, редкое — в «Ещё…», удаление —
  // в «Удалить…» (три пункта с подтверждением). Группа скрыта, если в ней пусто.
  $: deleteGroupActions = [
    ...deleteSheetActions,
    ...(canClearBelow(actionSheetMessage, messages, isChatOwner, hasMoreAfter, isGeneratingThisChat, isArchived)
      ? [{
          id: 'delete-below',
          label: TEXT.chat.deleteBelowAction,
          icon: 'trash',
          danger: true,
          confirm: TEXT.chat.deleteBelowConfirm,
        }]
      : []),
  ];
  $: deleteGroup = deleteGroupActions.length
    ? [{ id: 'delete-group', label: TEXT.chat.deleteGroup, icon: 'trash', danger: true, children: deleteGroupActions }]
    : [];
  $: replySheetAction = isArchived ? [] : [{ id: 'reply', label: TEXT.chat.replyAction, icon: 'reply' }];
  $: messageSheetActions = actionSheetMessage?.role === 'user'
    ? [
        ...replySheetAction,
        ...(canEditMessage(actionSheetMessage, myUserId, isChatOwner, isSharedChat, isGeneratingThisChat, isArchived)
          ? [{ id: 'edit', label: TEXT.edit.action, icon: 'rename' }]
          : []),
        { id: 'copy', label: TEXT.chat.copyAction, icon: 'copy' },
        { id: 'select-text', label: TEXT.chat.selectTextAction, icon: 'select' },
        { id: 'restore', label: 'В черновик', icon: 'draft' },
        {
          id: 'more',
          label: TEXT.chat.moreAction,
          children: [
            { id: 'new-chat', label: 'В новый чат', icon: 'new-chat' },
            {
              id: 'repeat',
              label: 'Повторить',
              icon: 'repeat',
              confirm: 'Повторить генерацию? Это новый запрос к модели.',
            },
          ],
        },
        ...deleteGroup,
      ]
    : [
        ...replySheetAction,
        ...(isMentionMode ? [{ id: 'reply-assistant', label: TEXT.family.replyToAssistant }] : []),
        { id: 'copy', label: TEXT.chat.copyAction, icon: 'copy' },
        { id: 'select-text', label: TEXT.chat.selectTextAction, icon: 'select' },
        { id: 'reuse', label: 'В черновик', icon: 'draft' },
        {
          id: 'more',
          label: TEXT.chat.moreAction,
          children: [
            { id: 'export-exchange', label: 'Экспорт пары', icon: 'export' },
            { id: 'print-exchange', label: TEXT.print.menuPrintExchange, icon: 'print' },
          ],
        },
        ...deleteGroup,
      ];

  async function handleMessageSheetSelect(event) {
    const message = actionSheetMessage;
    actionSheetMessage = null;
    if (!message) {
      return;
    }
    await handleMessageAction({ detail: { type: event.detail.id, message } });
  }

  // «Отчёт по проекту» (этап 23, реш. 27): экспорт folded — сводка, конспекты, живые сообщения.
  // Приглашённому позже часть скрыта — предупреждение перед выгрузкой.
  $: stagesHiddenForMe = Boolean(projectState?.hidden) || messages.some((item) => item.role === 'summary' && item.summaryHidden);
  $: reportAction = stages.length
    ? [{
        id: 'report',
        label: TEXT.print.menuReport,
        icon: 'export',
        ...(stagesHiddenForMe ? { confirm: TEXT.print.reportHiddenConfirm } : {}),
      }]
    : [];

  // ── Шторка меню чата (⋮ в шапке) ──
  // Владелец управляет чатом (rename/settings/delete/share); участник общего
  // чата — только просмотр состава, личный архив, экспорт и выход.
  // В7 (1.27.6): частое — сверху («Настройки чата» было десятым из одиннадцати и
  // уходило за край шторки), редкое — в подменю «Экспорт и печать…», сброс контекста —
  // здесь же, с подтверждением внутри шторки (раньше — системный диалог в настройках).
  $: exportGroup = {
    id: 'export-group',
    label: TEXT.print.menuExportGroup,
    icon: 'export',
    children: [
      { id: 'print', label: TEXT.print.menuPrint, icon: 'print' },
      { id: 'export', label: TEXT.print.menuMarkdown, icon: 'export' },
      ...reportAction,
    ],
  };
  $: chatMenuActions = isChatOwner
    ? [
        { id: 'settings', label: 'Настройки чата', icon: 'settings' },
        {
          id: 'rename',
          label: 'Переименовать',
          icon: 'rename',
          input: { value: chatTitle, placeholder: 'Название чата', submitLabel: 'Сохранить' },
        },
        { id: 'share', label: TEXT.family.shareTitle },
        muteAction,
        ...(canManageStages
          ? [{
              id: 'close-stage',
              label: TEXT.stages.closeAction,
              icon: 'archive',
              input: { value: '', placeholder: TEXT.stages.closeTitlePlaceholder, submitLabel: TEXT.stages.closeSubmit },
            }]
          : []),
        ...(isArchived
          ? []
          : [{ id: 'reset-context', label: TEXT.chat.resetContextAction, icon: 'repeat', confirm: TEXT.chat.resetContextConfirm }]),
        exportGroup,
        ...(publication?.available
          ? [{ id: 'publish', label: publication.published ? TEXT.publish.menuManage : TEXT.publish.menuPublish, icon: 'globe' }]
          : []),
        { id: 'archive', label: isArchived ? 'Вернуть из архива' : 'В архив', icon: 'archive' },
        { id: 'delete', label: 'Удалить', icon: 'trash', danger: true, confirm: 'Удалить чат? Восстановить не получится.' },
      ]
    : [
        { id: 'members', label: TEXT.family.membersTitle },
        muteAction,
        exportGroup,
        { id: 'archive', label: isArchived ? 'Вернуть из архива' : 'В архив', icon: 'archive' },
        { id: 'leave', label: TEXT.family.leaveChat, danger: true, confirm: TEXT.family.leaveConfirm },
      ];

  // ── Шторка выбора модели (1.25.7) ──
  // Открытие подтягивает свежий каталог: у владельца сервера в нём состояние моделей.
  function openModelPicker() {
    recentModels = loadRecentModels();
    modelPickerError = '';
    modelPickerOpen = true;
    loadModels().catch((e) => console.warn('Failed to refresh models', e));
  }

  async function pickModel(event) {
    const { id } = event.detail;
    if (id === sendModel) {
      modelPickerOpen = false;
      return;
    }
    const targetChatId = chatId;
    modelPickerBusy = id;
    modelPickerError = '';
    try {
      await api.updateChatSettings(targetChatId, { model: id });
      recentModels = rememberRecentModel(id);
      if (targetChatId === chatId) {
        currentModel = id;
        modelPickerOpen = false;
      }
    } catch (e) {
      modelPickerError = e?.status === 409
        ? TEXT.modelPicker.domesticRefused
        : classifyChatError(e, TEXT.modelPicker.failed);
    } finally {
      modelPickerBusy = null;
    }
  }

  function openChatSettingsFromPicker() {
    modelPickerOpen = false;
    window.location.hash = `/settings/${chatId}`;
  }

  async function handleChatMenuSelect(event) {
    const { id, value } = event.detail;
    chatMenuOpen = false;
    try {
      if (id === 'rename') {
        const nextTitle = (value || '').trim();
        if (nextTitle && nextTitle !== chatTitle) {
          await renameChat(chatId, nextTitle);
          showFlash('Чат переименован.', 'info');
        }
      } else if (id === 'archive') {
        await toggleArchive(chatId, !isArchived);
        showFlash(isArchived ? 'Чат возвращён из архива.' : 'Чат перенесён в архив.', 'info');
      } else if (id === 'close-stage') {
        await closeStage((value || '').trim() || null);
      } else if (id === 'print') {
        window.location.hash = `/print/${chatId}`;
      } else if (id === 'export') {
        handleExport('flat');
      } else if (id === 'report') {
        handleExport('folded');
      } else if (id === 'publish') {
        openPublication();
      } else if (id === 'settings') {
        window.location.hash = `/settings/${chatId}`;
      } else if (id === 'delete') {
        await deleteChat(chatId);
        showFlash('Чат удалён.', 'info');
        window.location.hash = '/chats';
      } else if (id === 'share' || id === 'members') {
        membersSheetOpen = true;
      } else if (id === 'notifications') {
        if (chatNotificationsOn) {
          await toggleChatNotifications(chatId, false);
        } else {
          // В12: разрешение, push-подписка и флаг — в этом же тапе.
          showFlash(chatNotificationsResultText(await enableChatNotifications(chatId)), 'info', 6000);
        }
      } else if (id === 'reset-context') {
        await api.resetChatContext(chatId);
        await refreshChatMetaQuietly(chatId, chatLoadSeq);
        showFlash(TEXT.settings.contextReset, 'info');
      } else if (id === 'leave') {
        await api.leaveChat(chatId);
        refreshChatsInBackground();
        showFlash('Вы вышли из чата.', 'info');
        window.location.hash = '/chats';
      }
    } catch (error) {
      statusMessage = classifyChatError(error, 'Не удалось выполнить действие.');
    }
  }

  // ── Удаление сообщений (этап 15) ──
  // Удаление физическое: превью чата, непрочитанные, поиск и контекст модели
  // считаются на сервере по таблице сообщений и пересчитываются сами.

  // Кто автор обмена: у ответа Петровича автор — тот, чей вопрос с тем же requestId.
  function exchangeAuthorId(message, list) {
    if (!message) {
      return null;
    }
    if (message.role === 'user') {
      return message.userId || null;
    }
    const question = list.find((item) => item.role === 'user' && item.requestId === message.requestId);
    return question?.userId || null;
  }

  // Аргументы передаём явно: Svelte отслеживает зависимости реактивного выражения
  // по прочитанным в нём переменным, а не внутри вызванной функции.
  function canDeleteMessage(message, list, owner, me, generating, activeId, archived) {
    // Архив — только для чтения (ТЗ §10.3): сервер отбивает удаление 409, и пункт
    // в шторке показывать незачем.
    if (archived) {
      return false;
    }
    if (!message || !message.id || message._streaming) {
      return false;
    }
    // Оптимистичное сообщение до подтверждения сервером: у него нет серверного id,
    // недоставленное убирается через «Повторить»/«Не доставлено».
    if (message.id.startsWith('local_')) {
      return false;
    }
    if (generating && activeId && message.requestId === activeId) {
      return false;
    }
    if (owner) {
      return true;
    }
    const author = exchangeAuthorId(message, list);
    return Boolean(author && author === me);
  }

  // Править можно только своё user-сообщение и не во время его генерации. В общем
  // чате обрезка задевает чужие реплики, поэтому там это доступно владельцу чата —
  // тем же правилом, по которому в общих чатах запрещён regen.
  function canEditMessage(message, me, owner, shared, generating, archived) {
    // Во время любой генерации в чате править нельзя: backend всё равно ответит 409.
    // Отдельной проверки по activeRequestId тут нет намеренно — он хранит ПОСЛЕДНИЙ
    // запрос и после ответа не обнуляется, из-за чего свежий обмен переставал
    // редактироваться (поймал e2e).
    if (archived || generating) {
      return false;
    }
    if (!message || !message.id || message._streaming || message.role !== 'user') {
      return false;
    }
    if (message.id.startsWith('local_')) {
      return false;
    }
    const author = message.userId || me;
    if (author !== me) {
      return false;
    }
    return shared ? Boolean(owner) : true;
  }

  // Сколько сообщений уйдёт из контекста: сам обмен и всё, что после него.
  function tailSize(message, list) {
    if (!message) {
      return 0;
    }
    const startIndex = list.findIndex((item) => item.requestId
      ? item.requestId === message.requestId
      : item.id === message.id);
    return startIndex === -1 ? 0 : list.length - startIndex;
  }

  // «Удалить всё выше» — только владельцу чата: участник видит историю не целиком
  // (visible_from_ts) и чистил бы то, чего не видит. И только если выше что-то есть.
  function canClearAbove(message, list, owner, more, generating, archived) {
    if (archived) {
      return false;
    }
    if (!message || !message.id || message._streaming || !owner || generating) {
      return false;
    }
    if (more) {
      return true;
    }
    return list.findIndex((item) => item.id === message.id) > 0;
  }

  // «Удалить всё ниже» (1.23.3) — зеркало «всё выше»: владельцу, вне генерации и архива,
  // и только если ниже обмена что-то есть (в ленте или за окном истории).
  function canClearBelow(message, list, owner, moreAfter, generating, archived) {
    if (archived) {
      return false;
    }
    if (!message || !message.id || message._streaming || !owner || generating) {
      return false;
    }
    if (moreAfter) {
      return true;
    }
    const index = list.findIndex((item) => item.id === message.id);
    if (index === -1) {
      return false;
    }
    const requestId = message.requestId;
    return list.some((item, position) => position > index && (!requestId || item.requestId !== requestId));
  }

  function describeDeleteError(error) {
    return error?.status === 409 ? TEXT.chat.deleteBusy : classifyChatError(error, TEXT.chat.deleteFailed);
  }

  // Снимок кэша и список чатов после удаления: превью и непрочитанные изменились.
  function afterLocalDeletion() {
    persistMessagesSnapshot();
    refreshChatsInBackground();
  }

  async function deleteMessageExchange(message) {
    const targetChatId = chatId;
    try {
      const result = await api.deleteMessage(targetChatId, message.id);
      if (isStaleChatLoad(targetChatId, chatLoadSeq)) {
        return;
      }
      const removed = new Set(result?.deletedIds?.length ? result.deletedIds : [message.id]);
      messages = messages.filter((item) => !removed.has(item.id));
      statusMessage = '';
      afterLocalDeletion();
    } catch (e) {
      console.warn('Failed to delete message', e);
      if (!isStaleChatLoad(targetChatId, chatLoadSeq)) {
        statusMessage = describeDeleteError(e);
      }
    }
  }

  async function deleteMessagesAbove(message) {
    const targetChatId = chatId;
    try {
      const result = await api.deleteMessagesBefore(targetChatId, message.id);
      if (isStaleChatLoad(targetChatId, chatLoadSeq)) {
        return;
      }
      applyDeletionBefore(result?.beforeTs);
      statusMessage = '';
      showFlash(TEXT.chat.deleteAboveDone, 'info');
      afterLocalDeletion();
    } catch (e) {
      console.warn('Failed to delete history above message', e);
      if (!isStaleChatLoad(targetChatId, chatLoadSeq)) {
        statusMessage = describeDeleteError(e);
      }
    }
  }

  async function deleteMessagesBelow(message) {
    const targetChatId = chatId;
    try {
      const result = await api.deleteMessagesAfter(targetChatId, message.id);
      if (isStaleChatLoad(targetChatId, chatLoadSeq)) {
        return;
      }
      applyDeletionAfter(result?.afterTs);
      statusMessage = '';
      showFlash(TEXT.chat.deleteBelowDone, 'info');
      afterLocalDeletion();
    } catch (e) {
      console.warn('Failed to delete history below message', e);
      if (!isStaleChatLoad(targetChatId, chatLoadSeq)) {
        statusMessage = describeDeleteError(e);
      }
    }
  }

  // Правка (1.24.1) идёт в полноэкранном редакторе поверх чата: композер, его
  // черновик и вложения не трогаются — отмена правки ничего не теряет.
  function startEditMessage(message) {
    editingMessage = message;
    editKeepTail = false;
    editError = '';
    // Композер оставался бы под редактором с поднятой клавиатурой.
    inputElement?.blur();
  }

  $: editImages = editingMessage
    ? messageImages(editingMessage)
      .slice(0, config.maxImagesPerMessage)
      .map((dataUrl) => ({ dataUrl }))
    : [];

  function cancelEditMessage() {
    editingMessage = null;
    editKeepTail = false;
    editError = '';
  }

  // Отправка из редактора: сначала убираем старый обмен и хвост, и только потом
  // шлём заново — иначе при отказе сервера в чате оказалось бы два вопроса подряд.
  async function submitEdit(event) {
    const { text, images, keepTail } = event.detail;
    editKeepTail = Boolean(keepTail);
    editError = '';
    if (!(await applyEditCut())) {
      return;
    }
    const sent = await sendPreparedMessage({ text, images, fromComposer: false });
    if (sent) {
      editingMessage = null;
      editKeepTail = false;
    } else {
      editError = statusMessage || TEXT.edit.failed;
    }
  }

  // Отрезать (или пометить) хвост перед повторной отправкой. Возвращает false,
  // если сервер отказал — тогда сообщение не уходит и правка остаётся на экране.
  async function applyEditCut() {
    const message = editingMessage;
    if (!message) {
      return true;
    }
    editBusy = true;
    try {
      if (editKeepTail) {
        const result = await api.excludeMessagesFrom(chatId, message.id);
        applyExclusionFrom(result?.fromTs);
      } else {
        const result = await api.deleteMessagesFrom(chatId, message.id);
        applyDeletionFrom(result?.fromTs);
      }
      afterLocalDeletion();
      return true;
    } catch (e) {
      console.warn('Failed to apply message edit', e);
      editError = e?.status === 409 ? TEXT.edit.busy
        : e?.status === 403 ? TEXT.edit.sharedOnlyOwner
        : classifyChatError(e, TEXT.edit.failed);
      return false;
    } finally {
      editBusy = false;
    }
  }

  function applyDeletionFrom(fromTs) {
    if (!fromTs) {
      return;
    }
    messages = messages.filter((item) => !(item.ts && item.ts >= fromTs));
  }

  // «Удалить всё ниже» (1.23.3): позже метки ничего нет — окно истории доходит до хвоста.
  function applyDeletionAfter(afterTs) {
    if (!afterTs) {
      return;
    }
    messages = messages.filter((item) => !(item.ts && item.ts > afterTs));
    if (windowMode === 'history' && !stageWindow) {
      hasMoreAfter = false;
      nextAfter = null;
      windowMode = 'tail';
    }
  }

  function applyExclusionFrom(fromTs) {
    if (!fromTs) {
      return;
    }
    messages = messages.map((item) => (
      item.ts && item.ts >= fromTs && !item.excludedFromContext
        ? { ...item, excludedFromContext: true }
        : item
    ));
  }

  // Выбросить из ленты всё раньше метки. Выше не осталось ничего, поэтому подгрузку
  // назад закрываем: иначе скролл вверх продолжал бы просить страницы, которых нет.
  function applyDeletionBefore(beforeTs) {
    if (!beforeTs) {
      return;
    }
    messages = messages.filter((item) => !(item.ts && item.ts < beforeTs));
    hasMore = false;
    nextBefore = null;
  }

  // Плавающая кнопка «Вниз»: вернуться в конец ленты и снова прилипнуть к низу.
  // Из окна истории (этап 13) — сначала вернуть хвост.
  function jumpToBottom() {
    if (windowMode === 'history') {
      returnToTail();
      return;
    }
    shouldStickToBottom = true;
    scrollToBottom();
  }

  // Этап 13: курсор вперёд из ответа сервера. Окно, не доходящее до хвоста → 'history'.
  function applyForwardCursor(result) {
    hasMoreAfter = Boolean(result?.hasMoreAfter);
    nextAfter = hasMoreAfter ? (result?.nextAfter || null) : null;
    // Окно этапа (этап 23) хвостом не заканчивается никогда — «Вниз» возвращает ленту.
    windowMode = hasMoreAfter || stageWindow ? 'history' : 'tail';
  }

  function releaseScrollUp() {
    holdingScrollUp = false;
  }

  // Кнопка «Вверх» (этап 13), тап: на экран выше, как PageUp. Упёрлись в верх окна —
  // handleScroll сам подгрузит предыдущую страницу.
  function pageUp() {
    if (!messagesContainer) {
      return;
    }
    const nextScrollTop = Math.max(
      0,
      messagesContainer.scrollTop - messagesContainer.clientHeight * PAGE_SCROLL_RATIO
    );
    messagesContainer.scrollTop = nextScrollTop;
    messagesContainer.dispatchEvent(new Event('scroll', { bubbles: true }));
  }

  // Этап 13.6: свой scroll anchoring. Сообщение выше экрана при настоящем рендере
  // (content-visibility: auto) меняет высоту — сдвигаем scrollTop на ту же дельту до
  // отрисовки, чтобы видимый контент остался на месте; прилипание не трогаем.
  function applyAnchorShift(delta) {
    if (!messagesContainer || !delta) {
      return;
    }
    if (shouldStickToBottom) {
      // У низа якорь — сам низ: браузер уже подрезал scrollTop под новую высоту,
      // и сдвиг на дельту увёл бы ленту вверх от последних сообщений (двойная
      // компенсация). Просто остаёмся внизу.
      scrollToBottom();
      return;
    }
    suppressStickUpdate = true;
    messagesContainer.scrollTop += delta;
    lastScrollTop = messagesContainer.scrollTop;
    lastScrollHeight = messagesContainer.scrollHeight;
    queueMicrotask(() => {
      suppressStickUpdate = false;
    });
  }

  function scrollToTopOfWindow() {
    if (!messagesContainer) {
      return;
    }
    suppressStickUpdate = true;
    messagesContainer.scrollTop = 0;
    lastScrollTop = 0;
    queueMicrotask(() => {
      suppressStickUpdate = false;
    });
  }

  // Кнопка «Вверх», удержание: в начало истории. Если вся история уже в ленте —
  // просто наверх без сети; иначе окно первой страницы и режим 'history'.
  async function jumpToStart() {
    holdingScrollUp = false;
    if (jumpingToStart || !chatId) {
      return;
    }
    shouldStickToBottom = false;
    if (scrollFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
    }
    if (!hasMore && windowMode === 'tail') {
      scrollToTopOfWindow();
      return;
    }

    const targetChatId = chatId;
    const loadSeq = chatLoadSeq;
    jumpingToStart = true;
    try {
      const result = await api.getMessagesFromStart(targetChatId, config.messagesPageSize, { stage: stageWindow?.stageId || null });
      if (isStaleChatLoad(targetChatId, loadSeq) || !result.items.length) {
        return;
      }
      messages = result.items;
      hasMore = Boolean(result.hasMore);
      nextBefore = result.nextBefore || null;
      applyForwardCursor(result);
      shouldStickToBottom = false;
      await tick();
      scrollToTopOfWindow();
    } catch (e) {
      console.warn('Failed to load history start', e);
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        statusMessage = classifyChatError(e, TEXT.chat.jumpToStartFailed);
      }
    } finally {
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        jumpingToStart = false;
      }
    }
  }

  // Догрузка вперёд в режиме 'history' — скролл к низу окна. Доехали до хвоста —
  // режим 'tail' и merge всего, что пришло по MQTT, пока окно было в истории.
  async function loadNewerMessages() {
    if (loadingNewer || windowMode !== 'history' || !hasMoreAfter || !nextAfter) {
      return;
    }
    const targetChatId = chatId;
    const loadSeq = chatLoadSeq;
    loadingNewer = true;
    try {
      const result = await api.getMessagesAfter(targetChatId, nextAfter, config.messagesPageSize, { stage: stageWindow?.stageId || null });
      if (isStaleChatLoad(targetChatId, loadSeq)) {
        return;
      }
      messages = mergeMessageLists(messages, result.items);
      applyForwardCursor(result);
      if (windowMode === 'tail') {
        reconcileLatestMessages(targetChatId, loadSeq);
      }
    } catch (e) {
      console.warn('Failed to load newer messages', e);
    } finally {
      if (!isStaleChatLoad(targetChatId, loadSeq)) {
        loadingNewer = false;
      }
    }
  }

  // Возврат к хвосту из окна истории: мгновенно из снимка кэша (он хранит именно хвост),
  // затем свежая последняя страница с сервера; прилипание включается.
  async function returnToTail() {
    const targetChatId = chatId;
    const loadSeq = ++chatLoadSeq;
    stageWindow = null;
    windowMode = 'tail';
    hasMoreAfter = false;
    nextAfter = null;
    loadingNewer = false;
    jumpingToStart = false;
    hasMore = false;
    nextBefore = null;
    const cached = readSnapshot(targetChatId);
    messages = cached;
    shouldStickToBottom = true;
    await loadMessages(true, {
      keepBottom: true,
      targetChatId,
      loadSeq,
      silent: cached.length > 0,
      ignoreFocus: true,
    });
  }

  function scrollToBottom() {
    if (messagesContainer) {
      suppressStickUpdate = true;
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      lastScrollTop = messagesContainer.scrollTop;
      lastScrollHeight = messagesContainer.scrollHeight;
      queueMicrotask(() => {
        suppressStickUpdate = false;
      });
    }
  }

  async function scrollToMessage(messageId) {
    if (!messageId || !messagesContainer) {
      return;
    }

    await tick();
    const target = messagesContainer.querySelector(`[data-message-id="${messageId}"]`);
    if (!target) {
      return;
    }

    shouldStickToBottom = false;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  async function focusSearchMessage(messageId) {
    if (!messageId || messageId === lastFocusedMessageId) {
      return;
    }

    lastFocusedMessageId = messageId;
    // Подсветка включает и принудительный рендер цели (is-forced-visible): прокрутка
    // центрирует по настоящей высоте, а не по оценочной от content-visibility (13.6).
    highlightedMessageId = messageId;
    await scrollToMessage(messageId);
    if (highlightTimer) {
      window.clearTimeout(highlightTimer);
    }
    highlightTimer = window.setTimeout(() => {
      highlightedMessageId = null;
      highlightTimer = null;
    }, 3500);
  }

  function handleMessagesKeydown(e) {
    if (!messagesContainer) {
      return;
    }

    let nextScrollTop = null;
    if (e.key === 'ArrowUp') {
      nextScrollTop = messagesContainer.scrollTop - KEYBOARD_SCROLL_STEP_PX;
    } else if (e.key === 'ArrowDown') {
      nextScrollTop = messagesContainer.scrollTop + KEYBOARD_SCROLL_STEP_PX;
    } else if (e.key === 'PageUp') {
      nextScrollTop = messagesContainer.scrollTop - messagesContainer.clientHeight * 0.9;
    } else if (e.key === 'PageDown') {
      nextScrollTop = messagesContainer.scrollTop + messagesContainer.clientHeight * 0.9;
    } else if (e.key === 'Home') {
      nextScrollTop = 0;
    } else if (e.key === 'End') {
      nextScrollTop = messagesContainer.scrollHeight;
    }

    if (nextScrollTop === null) {
      return;
    }

    e.preventDefault();
    messagesContainer.scrollTop = Math.max(0, nextScrollTop);
    messagesContainer.dispatchEvent(new Event('scroll', { bubbles: true }));
  }

  function enhanceMessagesKeyboard(node) {
    const focusNode = () => {
      if (typeof node.focus === 'function') {
        node.focus();
      }
    };

    node.addEventListener('keydown', handleMessagesKeydown);
    node.addEventListener('pointerdown', focusNode);

    return {
      destroy() {
        node.removeEventListener('keydown', handleMessagesKeydown);
        node.removeEventListener('pointerdown', focusNode);
      },
    };
  }

  function scheduleScrollToBottom() {
    if (typeof window === 'undefined' || !messagesContainer) {
      return;
    }
    if (scrollFrame !== null) {
      window.cancelAnimationFrame(scrollFrame);
    }
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = null;
      if (!shouldStickToBottom) {
        return;
      }
      scrollToBottom();
    });
  }

  function getDistanceFromBottom() {
    if (!messagesContainer) {
      return 0;
    }
    return messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight;
  }

  async function handleExport(stagesMode = 'flat') {
    // Экспортируем ПОЛНУЮ историю: раньше молча уходили только подгруженные
    // страницы (50 последних). Дотягиваем страницы назад с разумным капом.
    // Этап 23: «Экспорт в Markdown» — flat (всё подряд, конспекты на своих местах),
    // «Отчёт по проекту» — folded (сводка, конспекты вместо свёрнутых, живые).
    let exportMessages = messages;
    try {
      let before = null;
      let guard = 0;
      let collected = [];
      let hasOlder = true;
      while (hasOlder && guard < 40) {
        guard += 1;
        const page = await api.getMessages(chatId, config.messagesPageSize, before, { stages: stagesMode });
        const items = page?.items || [];
        if (!items.length) {
          break;
        }
        collected = [...items, ...collected];
        hasOlder = Boolean(page?.hasMore);
        before = page?.nextBefore || null;
        if (!before) {
          hasOlder = false;
        }
      }
      if (collected.length) {
        exportMessages = collected;
      }
    } catch (error) {
      console.warn('Export: failed to fetch full history, exporting loaded part', error);
    }
    const stateTitle = TEXT.stages.stateTitle;
    const content = exportToMarkdown(chatTitle, exportMessages, showSystemMessages, travelMeta, listMeta, {
      stages,
      projectState: stagesMode === 'folded' || stages.length ? projectState : null,
      stateTitle,
    });
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const safeName = chatTitle ? chatTitle.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_').slice(0, 50) : `chat-${chatId.slice(0, 8)}`;
    const filename = `${safeName}${stagesMode === 'folded' ? '-report' : ''}-${date}.md`;
    downloadFile(content, filename);
  }

  // Handle response completion
  async function handleResponse(e) {
    const payload = e.detail;
    if (payload.chatId !== chatId) return;

    upsertFinalResponse(payload);
    currentRequestId = null;
    statusMessage = '';
    await tick();
    if (shouldStickToBottom) {
      scheduleScrollToBottom();
    }
    reconcileLatestMessages();
    refreshTypeMetaAfterAnswer();
  }

  // Мета по типу после ответа — только своя (1.24.2): раньше /travel запрашивался у
  // чата любого типа и по response, и по status done — три раза на ответ. Чат,
  // ставший путевым по ходу разговора, подхватывается реактивно ниже: список чатов
  // после ответа обновляется, chatType в нём меняется.
  function refreshTypeMetaAfterAnswer() {
    if (isTravelChat) loadTravelMeta(chatId, chatLoadSeq);
    if (isScanChat) loadScanMeta(chatId, chatLoadSeq);
  }

  // Догрузка меты только на ПЕРЕХОД в путевой тип внутри открытого чата (список
  // обновился после ответа); при открытии уже путевого чата мету грузит обычный
  // путь через настройки — иначе /travel уходил дважды (прод-лог 1.24.2).
  let travelTypeSeen = null;
  $: {
    if (travelTypeSeen?.chatId !== chatId) {
      travelTypeSeen = { chatId, isTravel: isTravelChat };
    } else if (isTravelChat && !travelTypeSeen.isTravel) {
      travelTypeSeen = { chatId, isTravel: true };
      if (!travelMeta) {
        loadTravelMeta(chatId, chatLoadSeq);
      }
    } else if (travelTypeSeen.isTravel !== isTravelChat) {
      travelTypeSeen = { chatId, isTravel: isTravelChat };
    }
  }

  function handleStatus(e) {
    const payload = e.detail;
    if (payload.chatId !== chatId) {
      return;
    }
    if (payload.state === 'cancelled' || payload.state === 'idle') {
      currentRequestId = null;
    }
    // state=done: мету по типу грузит обработчик ответа — здесь второй раз не нужно.
  }

  function handleTech(e) {
    const payload = e.detail;
    if (payload.chatId !== chatId) {
      return;
    }
    if (payload.message === 'chat_archived') {
      isArchived = true;
    }
  }

  function handleDraftStorageChange(event) {
    if (!chatId || event.key !== getDraftKey(chatId)) {
      return;
    }

    const nextDraft = event.newValue || '';
    if (nextDraft === inputText) {
      return;
    }

    inputText = nextDraft;
    lastPersistedDraft = nextDraft;
    tick().then(() => syncInputHeight());
  }

  function syncDraftFromStorage() {
    if (!chatId) {
      return;
    }

    const nextDraft = getDraft(chatId) || '';
    if (nextDraft === inputText) {
      return;
    }

    inputText = nextDraft;
    lastPersistedDraft = nextDraft;
    tick().then(() => syncInputHeight());
  }

  function persistMessagesSnapshot() {
    // Снимок — только хвост: окно истории (этап 13) кэшировать нельзя.
    if (chatId && windowMode === 'tail') {
      writeSnapshot(chatId, messages);
    }
  }

  function scheduleSnapshotPersist() {
    if (typeof window === 'undefined') {
      return;
    }
    if (snapshotTimer) {
      window.clearTimeout(snapshotTimer);
    }
    snapshotTimer = window.setTimeout(() => {
      snapshotTimer = null;
      persistMessagesSnapshot();
    }, 800);
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      syncDraftFromStorage();
      // Вернулись к чату — всё, что на экране, прочитано.
      if (latestMessageTs) {
        scheduleMarkRead(latestMessageTs);
      }
    } else if (document.visibilityState === 'hidden') {
      // Уходим в фон — синхронно сохраняем снимок (дебаунс-таймер iOS может заморозить),
      // чтобы при следующем открытии показать чат мгновенно из кэша, без спиннера.
      if (snapshotTimer) {
        window.clearTimeout(snapshotTimer);
        snapshotTimer = null;
      }
      persistMessagesSnapshot();
    }
  }

  function handleMembersChanged(event) {
    refreshChatsInBackground();
    if (event.detail?.responseModeChanged) {
      showFlash(TEXT.family.mentionModeHint, 'info', 6000);
    }
  }

  // События семейных чатов (/event через stores.applyEventPayload).
  function handleFamilyEvent(e) {
    const payload = e.detail;
    if (payload?.type === 'models_changed' && chatId) {
      // Этап 33.2: модель чата могли выключить — backend уже перевёл чат на основную,
      // шапка берёт модель из настроек чата заново.
      refreshChatMetaQuietly(chatId, chatLoadSeq);
      return;
    }
    if (!payload || payload.chatId !== chatId) {
      return;
    }
    if (payload.type === 'message' && payload.message) {
      if (windowMode !== 'history') {
        messages = mergeMessageLists(messages, [payload.message]);
      }
      if (payload.message.userId) {
        // Пришло сообщение — участник уже не «печатает».
        const { [payload.message.userId]: _dropped, ...rest } = typingUntil;
        typingUntil = rest;
      }
      tick().then(() => {
        if (shouldStickToBottom) {
          scheduleScrollToBottom();
        }
      });
      return;
    }
    if (payload.type === 'message_deleted') {
      // Удалили у другого участника или на другом своём устройстве. Событие приходит
      // и автору действия — повторная фильтрация уже удалённого безвредна.
      const removed = new Set(payload.messageIds || []);
      if (removed.size) {
        messages = messages.filter((item) => !removed.has(item.id));
      }
      applyDeletionBefore(payload.beforeTs);
      applyDeletionFrom(payload.fromTs);
      applyDeletionAfter(payload.afterTs);
      persistMessagesSnapshot();
      return;
    }

    if (payload.type === 'messages_excluded') {
      // Правка «оставить хвост»: сообщения на месте, но из контекста ушли.
      applyExclusionFrom(payload.fromTs);
      persistMessagesSnapshot();
      return;
    }

    if (payload.type === 'stage') {
      applyStageEvent(payload);
      return;
    }

    if (payload.type === 'publication') {
      applyPublicationEvent(payload);
      return;
    }

    if (payload.type === 'typing') {
      noteTyping(payload.userId);
      return;
    }
    if (payload.type === 'members') {
      const affectsMe = (payload.userIds || []).includes(myUserId);
      if (payload.action === 'chat_deleted') {
        showFlash(TEXT.family.chatDeletedByOwner, 'info');
        window.location.hash = '/chats';
      } else if (payload.action === 'removed' && affectsMe) {
        showFlash(TEXT.family.removedFromChat, 'info');
        window.location.hash = '/chats';
      }
    }
  }

  function handleAppResumed() {
    // На возврате из фона НЕ пересоздаём список (это вызывало «флап»): неразрушающим
    // merge дотягиваем финальные ответы, пришедшие пока MQTT был мёртв (источник
    // истины — backend), и тихо обновляем настройки чата — всё без спиннера.
    if (!chatId) {
      return;
    }
    const targetChatId = chatId;
    const loadSeq = chatLoadSeq;
    reconcileLatestMessages(targetChatId, loadSeq);
    refreshChatMetaQuietly(targetChatId, loadSeq, { silent: true });
  }

  onMount(() => {
    window.addEventListener('mqtt:response', handleResponse);
    window.addEventListener('mqtt:status', handleStatus);
    window.addEventListener('mqtt:tech', handleTech);
    window.addEventListener('mqtt:event', handleFamilyEvent);
    window.addEventListener('storage', handleDraftStorageChange);
    window.addEventListener('focus', syncDraftFromStorage);
    window.addEventListener('app:resumed', handleAppResumed);
    window.addEventListener('app:viewport-resized', handleViewportResized);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Автофокус — только в пустом чате или при непустом черновике: при открытии
    // истории клавиатура iOS закрывала бы последние сообщения (сначала читают).
    if (inputElement) {
      // В3 (1.27.3): новый чат — клавиатура уже открыта заглушкой из тапа «Новый чат»,
      // переносим фокус в композер (сам focus() вне жеста iOS клавиатуру не открывает).
      if (!handOffKeyboard(inputElement) && (messages.length === 0 || inputText)) {
        inputElement.focus();
      }
      syncInputHeight(inputElement);
    }
  });

  // Уборка за собой (2026-09-18): «нажал +, передумал, вышел» оставлял пустой чат
  // в списке — таких накопилось 25 из 295. Удаляем только заведомо ненужное: обычный
  // чат без сообщений, без названия, без черновика, не общий и не архивный. Список
  // покупок, путевой чат и сканы живут без единой реплики — их не трогаем.
  function isAbandonedEmptyChat(meta) {
    if (!meta || messages.length || streamingText) {
      return false;
    }
    // Список чатов знает про последнее сообщение и без загруженной ленты — на него и
    // опираемся: из чата успевают выйти раньше, чем придёт ответ на /messages.
    if (meta.lastMessageTs || meta.lastMessagePreview) {
      return false;
    }
    if ((meta.chatType || 'default') !== 'default') {
      return false;
    }
    if (meta.title || meta.isArchived || meta.isShared || (meta.members?.length || 0) > 1) {
      return false;
    }
    if (meta.isOwner === false) {
      return false;
    }
    if (hasPendingContent || isGeneratingThisChat || activeRequestId) {
      return false;
    }
    return true;
  }

  // Переход в настройки, контекст или печать этого же чата — не выход из него.
  function leavingChatEntirely(id) {
    if (typeof window === 'undefined') {
      return true;
    }
    const hash = window.location.hash || '';
    return ![`/settings/${id}`, `/context/${id}`, `/print/${id}`].some((route) => hash.includes(route));
  }

  function dropEmptyChatOnExit(id, meta) {
    if (!id || !isAbandonedEmptyChat(meta) || !leavingChatEntirely(id)) {
      return;
    }
    // Тихо: пользователь уже ушёл, и сообщать ему не о чем.
    deleteChat(id).catch((error) => console.warn('Empty chat cleanup failed', error));
  }

  onDestroy(() => {
    if (statusTicker) {
      clearInterval(statusTicker);
      statusTicker = null;
    }
    if (scrollFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
    }
    if (highlightTimer) {
      window.clearTimeout(highlightTimer);
      highlightTimer = null;
    }
    if (snapshotTimer) {
      window.clearTimeout(snapshotTimer);
      snapshotTimer = null;
    }
    if (markReadTimer) {
      window.clearTimeout(markReadTimer);
      markReadTimer = null;
    }
    if (typingPruneTimer) {
      window.clearInterval(typingPruneTimer);
      typingPruneTimer = null;
    }
    // Сохраняем актуальный снимок при уходе с экрана чата (смена роута/размонтирование).
    persistMessagesSnapshot();
    dropEmptyChatOnExit(chatId, currentChatMeta);
    window.removeEventListener('mqtt:response', handleResponse);
    window.removeEventListener('mqtt:status', handleStatus);
    window.removeEventListener('mqtt:tech', handleTech);
    window.removeEventListener('mqtt:event', handleFamilyEvent);
    window.removeEventListener('storage', handleDraftStorageChange);
    window.removeEventListener('focus', syncDraftFromStorage);
    window.removeEventListener('app:resumed', handleAppResumed);
    window.removeEventListener('app:viewport-resized', handleViewportResized);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });

  // Scroll to bottom on new messages
  $: if (displayMessages.length > 0 && messagesContainer && shouldStickToBottom) {
    tick().then(scheduleScrollToBottom);
  }
  // Персист снимка (дебаунсом) для мгновенной гидрации при следующем открытии чата.
  $: if (chatId && messages.length && windowMode === 'tail') {
    scheduleSnapshotPersist();
  }
  $: if (inputElement) {
    tick().then(() => syncInputHeight(inputElement));
  }
  $: if (chatId && lastLoadedChatId !== chatId) {
    activateChat(chatId);
  }
  $: if (chatId && focusMessageId && focusMessageId !== lastFocusedMessageId && !loading) {
    const hasFocusedMessage = messages.some((message) => message.id === focusMessageId);
    if (hasFocusedMessage) {
      tick().then(() => focusSearchMessage(focusMessageId));
    } else {
      const loadSeq = ++chatLoadSeq;
      loadMessages(true, { keepBottom: false, targetChatId: chatId, loadSeq });
    }
  }
</script>

<div
  class="chat-page"
  bind:this={pageEl}
  use:pageEntrance
  use:swipe
  on:swipemove={onSwipeMove}
  on:swipeend={onSwipeEnd}
  on:swipeleft={openContextScreen}
  on:swiperight={goToChatList}
>
  <header class="header">
    <a href="#/chats" class="back-btn" aria-label="К списку чатов" on:click|preventDefault={goToChatList}>←</a>
    <div class="header-title">
      <div class="title-row">
        <h1>{chatTitle}</h1>
        {#if isArchived}
          <span class="archived-badge">Архив</span>
        {/if}
        {#if isTravelChat}
          <span class="travel-badge">Путевой</span>
        {/if}
        {#if isScanChat}
          <span class="travel-badge scan-badge">Скан</span>
        {/if}
        {#if isListChat}
          <span class="travel-badge list-badge">{TEXT.list.badge}</span>
        {/if}
        {#if isDachaBrain}
          <span class="travel-badge dacha-badge">{TEXT.dacha.badge}</span>
        {/if}
      </div>
      <div class="subline">
      {#if sendModel && canPickModel}
        <button
          type="button"
          class="model-line model-button"
          data-testid="model-button"
          aria-haspopup="dialog"
          aria-label={`${TEXT.modelPicker.open}: ${modelLabel(sendModel)}`}
          on:click={openModelPicker}
        >
          <span class="model-name">{modelLabel(sendModel)}</span><span class="model-caret" aria-hidden="true">▾</span>
        </button>
      {:else if sendModel}
        <p class="model-line">{modelLabel(sendModel)}</p>
      {/if}
      {#if publicationChip}
        <button
          type="button"
          class="pub-chip"
          data-tone={publicationChip.tone}
          data-testid="publication-chip"
          on:click={openPublication}
        >{publicationChip.label}</button>
      {/if}
      </div>
    </div>
    <div class="header-actions">
      <a href="#/context/{chatId}" class="icon-btn" aria-label={TEXT.dacha.contextButton} on:click|preventDefault={openContextScreen}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
          <path d="M4 7h9M19 7h1M4 12h3M13 12h7M4 17h11M21 17h-1"/>
          <circle cx="16" cy="7" r="2"/><circle cx="10" cy="12" r="2"/><circle cx="18" cy="17" r="2"/>
        </svg>
      </a>
      <button class="icon-btn" on:click={() => (chatMenuOpen = true)} aria-label="Меню чата" aria-haspopup="dialog">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
        </svg>
      </button>
    </div>
  </header>
  <BrainStatusLine />

  {#if isListChat}
    <ListPanel
      {chatId}
      snapshot={listMeta}
      readOnly={isArchived}
      roster={$usersStore}
      isShared={isSharedChat}
      on:change={(e) => (listMeta = e.detail)}
    />
  {/if}

  {#if isTravelChat}
    <section class="travel-panel" aria-label="Путевой чат">
      <div class="travel-panel-main">
        <div class="travel-panel-heading">
          <div class="travel-panel-title">Путевой чат</div>
          {#if hasTravelDetails}
            <button
              type="button"
              class="travel-toggle-btn"
              aria-expanded={!travelPanelCollapsed}
              aria-controls="travel-details"
              aria-label={travelPanelCollapsed ? 'Показать детали поездки' : 'Скрыть детали поездки'}
              on:click={toggleTravelPanel}
            >
              {travelPanelCollapsed ? 'Показать' : 'Скрыть'}
            </button>
          {/if}
        </div>
        {#if !travelPanelCollapsed}
          <div id="travel-details" class="travel-profile">
            {#each travelProfileRowsList as row}
              <span><strong>{row[0]}:</strong> {row[1]}</span>
            {/each}
            {#if !travelProfileRowsList.length}
              <span>Профиль поездки уточняется по переписке.</span>
            {/if}
          </div>
        {/if}
      </div>
      {#if travelMeta?.places?.length && !travelPanelCollapsed}
        <div class="travel-places">
          {#each travelMeta.places as place}
            <article class="travel-place">
              {#if editingPlaceId === place.placeId}
                <input class="place-input" bind:value={placeEditTitle} maxlength="120" aria-label="Название места" />
                <textarea class="place-textarea" bind:value={placeEditReason} maxlength="1000" rows="2" aria-label="Почему место выбрано"></textarea>
                <textarea class="place-textarea" bind:value={placeEditNote} maxlength="1000" rows="2" aria-label="Заметка"></textarea>
                {#if placeActionError}
                  <div class="place-error">{placeActionError}</div>
                {/if}
                <div class="place-actions">
                  <button type="button" on:click={() => savePlace(place)}>Сохранить</button>
                  <button type="button" on:click={closePlaceEditor}>Отмена</button>
                  <button type="button" class="danger" on:click={() => tapRowDelete(`place:${place.placeId}`) && removePlace(place)}>
                    {$rowDeleteArmed === `place:${place.placeId}` ? TEXT.chat.deleteArmed : 'Удалить'}
                  </button>
                </div>
              {:else}
                {#if place.photoUrl}
                  <img class="place-photo" src={place.photoUrl} alt={`Фото: ${place.title}`} loading="lazy" />
                {/if}
                <div class="place-heading">
                  {#if getTravelPlaceMapsUrl(place)}
                    <a href={getTravelPlaceMapsUrl(place)} target="_blank" rel="noopener noreferrer">{place.title}</a>
                  {:else}
                    <strong>{place.title}</strong>
                  {/if}
                  <span>{placeStatusLabel(place.enrichmentStatus)}</span>
                </div>
                {#if place.address}
                  <div class="place-meta">{place.address}</div>
                {/if}
                {#if placeCoordinates(place)}
                  <div class="place-meta">
                    {#if getTravelPlaceMapsUrl(place)}
                      <a href={getTravelPlaceMapsUrl(place)} target="_blank" rel="noopener noreferrer">{placeCoordinates(place)}</a>
                    {:else}
                      {placeCoordinates(place)}
                    {/if}
                  </div>
                {/if}
                {#if place.selectionReason}
                  <div class="place-detail">{place.selectionReason}</div>
                {/if}
                {#if place.note}
                  <div class="place-note">{place.note}</div>
                {/if}
                {#if place.photoSourceUrl}
                  <a class="place-photo-source" href={place.photoSourceUrl} target="_blank" rel="noopener noreferrer">
                    {place.photoAttribution || place.photoSourceTitle || 'Источник фото'}
                  </a>
                {/if}
                <button type="button" class="place-edit-btn" on:click={() => openPlaceEditor(place)}>Изменить</button>
              {/if}
            </article>
          {/each}
        </div>
      {/if}
    </section>
  {/if}

  {#if isScanChat}
    <section class="travel-panel scan-panel" aria-label="Скан-чат">
      <div class="travel-panel-main">
        <div class="travel-panel-heading">
          <div class="travel-panel-title">Собранный документ</div>
          {#if scanFragments.length}
            <button type="button" class="scan-analyze-btn" on:click={analyzeScanBlock} disabled={!canSendThisChat}>
              Анализировать
            </button>
          {/if}
        </div>
        <div class="travel-profile">
          {#if scanFragments.length}
            <span>Фрагментов: {scanFragments.length}. Фотографируйте дальше — распознанное добавится в документ.</span>
          {:else}
            <span>Сфотографируйте текст или схему кнопкой 📷 — распознанное соберётся в документ для анализа.</span>
          {/if}
        </div>
      </div>
      {#if scanFragments.length}
        <div class="travel-places">
          {#each scanFragments as fragment, i (fragment.fragmentId)}
            <article class="travel-place">
              {#if editingFragmentId === fragment.fragmentId}
                <input class="place-input" bind:value={fragmentEditTitle} maxlength="200" aria-label="Заголовок фрагмента" />
                <textarea class="place-textarea" bind:value={fragmentEditContent} rows="6" aria-label="Распознанный текст"></textarea>
                {#if fragmentActionError}
                  <div class="place-error">{fragmentActionError}</div>
                {/if}
                <div class="place-actions">
                  <button type="button" on:click={() => saveFragment(fragment)}>Сохранить</button>
                  <button type="button" on:click={closeFragmentEditor}>Отмена</button>
                  <button type="button" class="danger" on:click={() => tapRowDelete(`fragment:${fragment.fragmentId}`) && removeFragment(fragment)}>
                    {$rowDeleteArmed === `fragment:${fragment.fragmentId}` ? TEXT.chat.deleteArmed : 'Удалить'}
                  </button>
                </div>
              {:else}
                <div class="place-heading">
                  <strong>{fragment.title || `Фрагмент ${i + 1}`}</strong>
                </div>
                <div class="scan-fragment-content">{fragment.content}</div>
                <button type="button" class="place-edit-btn" on:click={() => openFragmentEditor(fragment)}>Изменить</button>
              {/if}
            </article>
          {/each}
        </div>
      {/if}
    </section>
  {/if}

  <div class="messages-shell">
    {#if stageWindow}
      <div class="stage-window-bar" role="status" data-testid="stage-window-bar">
        <span class="stage-window-title">{stageWindowTitle}</span>
        <button type="button" class="stage-window-back" on:click={returnToTail}>{TEXT.stages.windowBack}</button>
      </div>
    {/if}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
  <div
    class="messages-area"
    bind:this={messagesContainer}
    data-scroll-root
    on:scroll={handleScroll}
    on:click={handleMessagesAreaClick}
    use:enhanceMessagesKeyboard
    tabindex="-1"
    role="log"
    aria-label="Сообщения чата"
    aria-live={isGeneratingThisChat ? 'off' : 'polite'}
  >
    {#if loading}
      <div class="loading-state">
        <div class="spinner"></div>
      </div>
    {:else if error}
      <div class="error-state">
        <p>{error}</p>
        <button on:click={() => loadMessages(true)}>Повторить</button>
      </div>
    {:else}
      {#if loadingMore}
        <div class="loading-more">
          <div class="spinner small"></div>
        </div>
      {/if}
      
      {#if displayMessages.length === 0}
        <div class="empty-state">
          <p>Начните диалог</p>
          <!-- Чипы прячутся, как только в композере появился текст (в т.ч. черновик
               из другой вкладки) — подсказка не должна затирать начатое сообщение. -->
          {#if !inputText.trim()}
          <div class="suggestion-chips">
            {#each EMPTY_CHAT_SUGGESTIONS as suggestion}
              <button type="button" class="suggestion-chip" on:click={() => applySuggestion(suggestion)}>
                {suggestion}
              </button>
            {/each}
          </div>
          {/if}
        </div>
      {:else}
        <div class="messages-list">
          {#each displayMessages as message, index (message.id)}
            {#if dateBoundaryBefore(message, index)}
              <div class="date-separator">{formatDateLabel(message.ts)}</div>
            {/if}
            {#if contextBoundaryBefore(message, index)}
              <div class="context-reset-marker">{contextResetLabel}</div>
            {/if}
            <div
              class="message-entry"
              class:highlighted={highlightedMessageId === message.id}
              class:is-forced-visible={highlightedMessageId === message.id}
              data-message-id={message.id}
              use:anchorHeight={{ shift: applyAnchorShift }}
            >
              {#if message.role === 'summary'}
                <!-- Этап 23: карточка этапа вместо свёрнутых сообщений. -->
                <StageCard
                  stage={stageForMessage(message)}
                  summary={message._placeholder ? null : message}
                  number={stageNumber(stages, stageIdOfSummary(message))}
                  isOwner={canManageStages}
                  gitImportedAt={gitImportedByStage[stageIdOfSummary(message)] || null}
                  on:action={handleStageAction}
                />
              {:else}
                <Message 
                  {message} 
                  isStreaming={message._streaming}
                  deliveryState={message.role === 'user' && message.requestId ? ($deliveryStore[message.requestId] ?? null) : null}
                  author={foreignAuthorOf(message, $usersStore, myUserId, isSharedChat)}
                  readByAll={isSharedChat && isReadByAll(message, chatMembers, myUserId)}
                  quickActions={quickActionsFor(message, quickTarget, canRegenerate, canShareText, statusRetryRequestId, $deliveryStore, currentRequestId)}
                  {showSystemMessages}
                  on:action={handleMessageAction}
                />
              {/if}
            </div>
          {/each}
        </div>
      {/if}
      {#if loadingNewer}
        <div class="loading-more">
          <div class="spinner small"></div>
        </div>
      {/if}
    {/if}
  </div>

  {#if showScrollUp}
    <button
      type="button"
      class="scroll-fab scroll-fab--up"
      class:is-holding={holdingScrollUp}
      class:is-busy={jumpingToStart}
      use:longpress
      on:pointerdown={() => { holdingScrollUp = true; }}
      on:pointerup={releaseScrollUp}
      on:pointercancel={releaseScrollUp}
      on:pointerleave={releaseScrollUp}
      on:click={pageUp}
      on:longpress={jumpToStart}
      aria-label={TEXT.chat.scrollUpAria}
      aria-busy={jumpingToStart}
      title={TEXT.chat.scrollUpTitle}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 19V5M5 12l7-7 7 7"/>
      </svg>
    </button>
  {/if}
  {#if !shouldStickToBottom && !loading && !error}
    <button type="button" class="scroll-fab" on:click={jumpToBottom} aria-label={TEXT.chat.scrollDownAria}>
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 5v14M5 12l7 7 7-7"/>
      </svg>
    </button>
  {/if}
  </div>

  {#if currentStatus && currentStatus.chatId === chatId}
    <div
      class="status-bar"
      role={currentStatus.state === 'error' ? 'alert' : 'status'}
      class:error={currentStatus.state === 'error'}
      class:generating={currentStatus.state === 'streaming'}
    >
      <span class="status-indicator"></span>
      <span class="status-text">
        {#if currentStatus.state === 'processing' && currentStatus.message}
          <!-- Этап 10: дача сообщает, чем занят Петрович («проверяет дом… (read_sensors)»). -->
          {describeStatusMessage(currentStatus.message)}
        {:else if currentStatus.state === 'queued' && currentStatus.message === WAITING_FOR_BRAIN}
          <!-- Этап 25.2: ни одно звено не ответило — запрос ждёт «мозг» до 5 минут. -->
          {describeStatusMessage(currentStatus.message)}
        {:else if currentStatus.state === 'queued'}
          {TEXT.chat.statusQueued}
        {:else if currentStatus.state === 'processing'}
          {statusSeconds >= THINKING_COUNTER_FROM_S
            ? TEXT.chat.statusThinkingFor.replace('{seconds}', String(statusSeconds))
            : TEXT.chat.statusThinking}
        {:else if currentStatus.state === 'received'}
          {TEXT.chat.statusSent}
        {:else if currentStatus.state === 'streaming'}
          {TEXT.chat.statusStreaming}
        {:else if currentStatus.state === 'error'}
          {TEXT.chat.statusErrorPrefix} {describeStatusMessage(currentStatus.message) || TEXT.status.unknown}
        {:else if currentStatus.state === 'cancelled'}
          {TEXT.chat.statusCancelled}
        {:else if currentStatus.state === 'done'}
          {TEXT.chat.statusDone}
        {/if}
      </span>
      {#if statusRetryRequestId}
        <button type="button" class="status-retry" on:click={() => regenerateAnswer(statusRetryRequestId)} aria-label={TEXT.chat.retryAria}>
          {TEXT.chat.retryAction}
        </button>
      {/if}
      {#if isGeneratingThisChat}
        <button type="button" class="stop-btn" on:click={handleCancel} aria-label="Остановить генерацию">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <rect x="8" y="8" width="8" height="8" rx="1.5" fill="currentColor"/>
          </svg>
        </button>
      {/if}
    </div>
  {/if}

  {#if typingLabel}
    <div class="status-bar typing" role="status">
      <span class="status-indicator"></span>
      <span class="status-text">{typingLabel}</span>
    </div>
  {/if}

  {#if statusMessage && (!currentStatus || currentStatus.state === 'error' || currentStatus.state === 'cancelled')}
    <div class="status-bar" role="status" class:error={currentStatus?.state === 'error'}>
      <span class="status-text">{statusMessage}</span>
    </div>
  {/if}

  <div class="input-area">
    {#if isArchived}
      <div class="archived-notice">
        Чат в архиве. Верните его из архива, чтобы продолжить.
      </div>
    {:else}
      {#if selectedImages.length}
        <div class="attachment-preview-list">
          {#each selectedImages as image, i (i)}
            <div class="attachment-preview">
              <img class="attachment-preview-image" src={image.dataUrl} alt={`Предпросмотр вложения ${i + 1}`} />
              <button
                type="button"
                class="attachment-thumb-remove"
                title="Убрать изображение"
                aria-label="Убрать изображение"
                on:click={() => removeImageAt(i)}
              >×</button>
            </div>
          {/each}
          {#if selectedImages.length > 1}
            <button type="button" class="attachment-clear-btn" on:click={clearSelectedImages}>
              Убрать все ({selectedImages.length})
            </button>
          {/if}
        </div>
        {#if visionModelTitle}
          <div class="vision-model-hint">Изображения анализирует: {visionModelTitle}</div>
        {/if}
      {/if}
      {#if attachmentError}
        <div class="offline-notice error-notice">
          {attachmentError}
        </div>
      {/if}
      {#if yandexLinksInDraft.length || explicitPlacesInDraft.length}
        <div class="travel-draft-notice">
          Точка интереса будет сохранена в путевой чат.
        </div>
      {/if}
      {#if showStageHint}
        <!-- Этап 23 (реш. 9): тихая подсказка владельцу — начало этапа уже вне окна модели. -->
        <div class="stage-hint" role="status" data-testid="stage-hint">
          <span>{TEXT.stages.hintText}</span>
          <button type="button" class="stage-hint-btn primary" on:click={() => closeStage(null)}>{TEXT.stages.hintFold}</button>
          <button type="button" class="stage-hint-btn" on:click={dismissHint}>{TEXT.stages.hintDismiss}</button>
        </div>
      {/if}
      {#if includeStages.length}
        <!-- Этап 23 (реш. 22): этапы, вложенные в следующий запрос целиком. -->
        <div class="include-chips" data-testid="include-chips">
          {#each includeStages as item (item.stageId)}
            <span class="include-chip">
              {TEXT.stages.includeChip.replace('{title}', stageTitle(item, stageNumber(stages, item.stageId), TEXT.stages.cardTitle))}
              <button type="button" class="include-remove" aria-label={TEXT.stages.includeRemove} on:click={() => removeIncludeStage(item.stageId)}>×</button>
            </span>
          {/each}
        </div>
      {/if}
      <div class="input-row">
        <!-- Поле на всю ширину: «+» (и камера в скан-чате) — внутри слева, бейдж
             Петровича — внутри справа, кнопка отправки появляется только когда
             есть что отправлять (текст или вложение). -->
        <div class="composer-field" class:with-camera={isScanChat} class:with-badge={isMentionMode && canSendThisChat}>
          <label class="attach-btn inline-btn inline-attach" aria-label="Прикрепить изображение" title="Прикрепить изображение">
            <input
              bind:this={imageInput}
              type="file"
              accept="image/*"
              multiple
              on:change={handleImageChange}
              disabled={!canSendThisChat}
            />
            <span>＋</span>
          </label>
          {#if isScanChat}
            <label class="attach-btn camera-btn inline-btn inline-camera" aria-label="Сфотографировать текст или схему" title="Сфотографировать текст или схему">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                on:change={handleCameraCapture}
                disabled={isArchived}
              />
              <span>📷</span>
            </label>
          {/if}
          <textarea
            bind:this={inputElement}
            bind:value={inputText}
            on:input={handleInput}
            placeholder={composerPlaceholder}
            rows="1"
            enterkeyhint="enter"
            autocapitalize="sentences"
            disabled={!canSendThisChat}
          ></textarea>
          {#if isMentionMode && canSendThisChat}
            <!-- Бейдж «Позвать Петровича» внутри поля: mousedown с preventDefault
                 держит фокус, иначе iOS прячет клавиатуру при тапе. -->
            <button
              type="button"
              class="inline-btn assistant-badge"
              class:active={callAssistant}
              aria-pressed={callAssistant}
              aria-label={callAssistant ? TEXT.family.callAssistantActive : TEXT.family.callAssistant}
              title={callAssistant ? TEXT.family.callAssistantActive : TEXT.family.callAssistant}
              on:mousedown|preventDefault
              on:click={toggleCallAssistant}
            >
              <img src="/icon-192.png" alt="" />
            </button>
          {/if}
        </div>
        {#if hasPendingContent && canSendThisChat}
          <button
            class="send-btn"
            class:to-assistant={callAssistant}
            use:longpress
            on:longpress={sendToAssistant}
            on:mousedown|preventDefault
            on:click={sendMessage}
            aria-label="Отправить сообщение"
            title={isMentionMode ? TEXT.family.sendToAssistantHint : ''}
          >
            {#if callAssistant}
              <img src="/icon-192.png" alt="" />
            {:else}
              →
            {/if}
          </button>
        {/if}
      </div>
    {/if}
  </div>

  <ActionSheet
    open={actionSheetMessage !== null}
    title={messagePreviewForSheet(actionSheetMessage)}
    actions={messageSheetActions}
    on:select={handleMessageSheetSelect}
    on:close={() => (actionSheetMessage = null)}
  />

  <ActionSheet
    open={Boolean(stageSheetStage)}
    title={stageSheetStage ? stageTitle(stageSheetStage, stageNumber(stages, stageSheetStage.stageId), TEXT.stages.cardTitle) : ''}
    actions={stageSheetActions}
    on:select={handleStageSheetSelect}
    on:close={() => { stageSheetStage = null; stageSheetSummary = null; }}
  />
  <MessageTextViewer
    open={textViewer !== null}
    title={textViewer?.title || ''}
    text={textViewer?.text || ''}
    mode={textViewer?.mode || 'plain'}
    on:copy={handleTextViewerCopy}
    on:close={() => (textViewer = null)}
  />
  <!-- Правка (этап 22, с 1.24.1 — полноэкранно): видно, что уйдёт из контекста,
       и чем — удалением или пометкой. Решение принимается до отправки. -->
  <MessageEditor
    open={editingMessage !== null}
    text={editingMessage?.text || ''}
    images={editImages}
    tailCount={editTailCount}
    busy={editBusy}
    error={editError}
    on:submit={submitEdit}
    on:cancel={cancelEditMessage}
  />
  <BottomSheet open={stageEditOpen} title={TEXT.stages.editTitle} on:close={() => (stageEditOpen = false)}>
    <div class="stage-edit" data-testid="stage-edit">
      <label class="stage-edit-label" for="stage-edit-title">{TEXT.stages.editTitleLabel}</label>
      <input id="stage-edit-title" type="text" maxlength="60" bind:value={stageEditTitle} disabled={stageEditBusy} />
      <label class="stage-edit-label" for="stage-edit-summary">{TEXT.stages.editSummaryLabel}</label>
      <textarea id="stage-edit-summary" rows="10" bind:value={stageEditSummary} disabled={stageEditBusy}></textarea>
      <p class="stage-edit-hint">{TEXT.stages.editLinksHint}</p>
      <button type="button" class="stage-edit-save" on:click={saveStageEdit} disabled={stageEditBusy}>{TEXT.stages.editSave}</button>
    </div>
  </BottomSheet>
  <ActionSheet
    open={chatMenuOpen}
    title={chatTitle}
    actions={chatMenuActions}
    on:select={handleChatMenuSelect}
    on:close={() => (chatMenuOpen = false)}
  />

  <BottomSheet open={publicationOpen} title={TEXT.publish.title} on:close={() => (publicationOpen = false)}>
    {#if publication}
      <PublicationPanel
        {chatId}
        {publication}
        {stages}
        hasState={Boolean(projectState?.summary)}
        {articleTick}
        on:changed={handlePublicationChanged}
        on:close-stage-and-publish={closeStageAndPublish}
      />
    {/if}
  </BottomSheet>

  <ModelPickerSheet
    open={modelPickerOpen}
    models={$modelsStore}
    current={sendModel}
    noDomestic={noDomesticFallback}
    recent={recentModels}
    busyId={modelPickerBusy}
    error={modelPickerError}
    on:select={pickModel}
    on:settings={openChatSettingsFromPicker}
    on:close={() => (modelPickerOpen = false)}
  />

  <ChatMembersSheet
    open={membersSheetOpen}
    {chatId}
    members={chatMembers}
    isOwner={isChatOwner}
    roster={$usersStore}
    myUserId={myUserId}
    on:changed={handleMembersChanged}
    on:close={() => (membersSheetOpen = false)}
  />
</div>

<style>
  .chat-page {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    /* Горизонталь отдана свайпу навигации (actions/swipe.js), вертикаль — скроллу ленты. */
    touch-action: pan-y;
  }

  .header {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    min-height: var(--header-height);
  }

  .back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    /* 44px — минимальная цель касания (HIG); отрицательный margin сохраняет
       прежнюю визуальную плотность шапки. */
    width: 44px;
    height: 44px;
    margin: -6px;
    color: var(--text-primary);
    font-size: 1.25rem;
    text-decoration: none;
    border-radius: 8px;
  }

  .back-btn:hover {
    background: var(--bg-tertiary);
  }

  .header-title {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    width: 100%;
  }

  .header-title h1 {
    font-size: 1rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .model-line {
    font-size: 0.75rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }

  /* Название модели — кнопка шторки выбора. Высота строки прежняя, а зона нажатия
     добрана отрицательными полями до ~32px, чтобы шапка не выросла. */
  .model-button {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    width: auto;
    max-width: 100%;
    /* В11 (1.27.2): зона нажатия ~40px по высоте, шапка не растёт. */
    margin: -0.65rem -0.4rem;
    padding: 0.65rem 0.4rem;
    border: none;
    background: none;
    text-align: left;
    border-radius: 6px;
    cursor: pointer;
  }

  .model-button .model-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Строка под названием: модель и значок публикации (этап 26.4). */
  .subline {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
    width: 100%;
  }

  .subline .model-line {
    flex: 0 1 auto;
    min-width: 0;
    width: auto;
  }

  .pub-chip {
    flex: none;
    font-size: 0.7rem;
    line-height: 1.3;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    border: 1px solid var(--border-color);
    background: none;
    color: var(--text-muted);
    white-space: nowrap;
  }

  .pub-chip[data-tone='ok'] {
    color: var(--success-color, #2e9d5b);
    border-color: currentColor;
  }

  .pub-chip[data-tone='git'] {
    color: var(--accent-color);
    border-color: currentColor;
  }

  .pub-chip[data-tone='danger'] {
    color: var(--error-text);
    border-color: currentColor;
  }

  .model-caret {
    flex: none;
    font-size: 0.65rem;
    opacity: 0.8;
  }

  .archived-badge {
    font-size: 0.75rem;
    padding: 0.125rem 0.5rem;
    background: var(--bg-tertiary);
    color: var(--text-muted);
    border-radius: 4px;
  }

  .travel-badge {
    font-size: 0.75rem;
    padding: 0.125rem 0.5rem;
    background: rgba(14, 165, 233, 0.14);
    color: #0284c7;
    border-radius: 4px;
  }

  .scan-badge {
    background: rgba(139, 92, 246, 0.16);
    color: #7c3aed;
  }

  .list-badge {
    background: rgba(90, 140, 255, 0.16);
    color: #5a8cff;
  }

  .dacha-badge {
    background: rgba(101, 163, 13, 0.16);
    color: #4d7c0f;
  }

  .scan-fragment-content {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.85rem;
    line-height: 1.45;
    max-height: 260px;
    overflow-y: auto;
  }

  .scan-analyze-btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.6rem;
    border: none;
    border-radius: 6px;
    background: rgba(139, 92, 246, 0.16);
    color: #7c3aed;
    cursor: pointer;
  }
  .scan-analyze-btn:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;
  }

  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    /* В11 (1.27.2): цель касания 44px (HIG); отрицательный margin сохраняет плотность шапки. */
    width: 44px;
    height: 44px;
    margin: -6px;
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: 1.125rem;
    border-radius: 8px;
    text-decoration: none;
    cursor: pointer;
  }

  .icon-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  .travel-panel {
    display: grid;
    gap: 0.75rem;
    padding: 0.7rem var(--spacing-lg);
    background: var(--bg-primary);
    border-bottom: 1px solid var(--border-light);
  }

  .travel-panel-main {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  .travel-panel-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-width: 0;
  }

  .travel-panel-title {
    color: var(--text-primary);
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .travel-toggle-btn {
    flex: 0 0 auto;
    border: 1px solid var(--border-light);
    border-radius: 6px;
    padding: 0.3rem 0.55rem;
    background: var(--bg-secondary);
    color: var(--text-secondary);
    font-size: 0.75rem;
    line-height: 1;
  }

  .travel-toggle-btn:hover {
    color: var(--text-primary);
    background: var(--bg-tertiary);
  }

  .travel-profile,
  .travel-places {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 0.65rem;
    color: var(--text-secondary);
    font-size: 0.75rem;
  }

  .travel-places {
    max-height: min(28vh, 18rem);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-right: 0.15rem;
  }

  .travel-profile span,
  .travel-places a,
  .travel-places span {
    overflow-wrap: anywhere;
  }

  .travel-profile strong {
    color: var(--text-muted);
    font-weight: 500;
  }

  .travel-places a {
    color: var(--accent-color);
    text-decoration: none;
  }

  .travel-places .place-photo-source {
    color: var(--text-muted);
    font-size: 0.6875rem;
    overflow-wrap: anywhere;
    text-decoration: none;
  }

  .travel-place {
    display: grid;
    gap: 0.25rem;
    min-width: min(100%, 15rem);
    max-width: 22rem;
    padding: 0.55rem;
    border: 1px solid var(--border-light);
    border-radius: 8px;
    background: var(--bg-secondary);
  }

  .place-photo {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    border-radius: 6px;
    background: var(--bg-tertiary);
  }

  @media (max-width: 640px) {
    .travel-places {
      max-height: min(34vh, 15rem);
    }

    .travel-place {
      max-width: none;
      width: 100%;
    }
  }

  .place-heading {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    justify-content: space-between;
  }

  .place-heading span {
    flex: 0 0 auto;
    color: var(--text-muted);
    font-size: 0.6875rem;
  }

  .place-meta,
  .place-detail,
  .place-note,
  .place-error {
    color: var(--text-secondary);
    font-size: 0.75rem;
    overflow-wrap: anywhere;
  }

  .place-note {
    color: var(--text-muted);
  }

  .place-error {
    color: var(--error-text);
  }

  .place-input,
  .place-textarea {
    width: 100%;
    border: 1px solid var(--border-light);
    border-radius: 6px;
    padding: 0.4rem 0.5rem;
    background: var(--bg-primary);
    color: var(--text-primary);
    font: inherit;
    /* ≥16px (1.27.8): мельче iOS увеличивала страницу при фокусе. */
    font-size: max(16px, 1rem);
    resize: vertical;
  }

  .place-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .place-actions button,
  .place-edit-btn {
    border: 1px solid var(--border-light);
    border-radius: 6px;
    padding: 0.32rem 0.5rem;
    background: var(--bg-primary);
    color: var(--text-secondary);
    font-size: 0.75rem;
  }

  .place-actions .danger {
    color: var(--error-text);
  }

  .messages-area {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    /* Этап 13.6: браузерный scroll anchoring выключен — свой в actions/anchor-height.js
       (WebKit anchoring не умеет, так Chromium и Safari ведут себя одинаково). */
    overflow-anchor: none;
    padding: var(--spacing-lg);
  }

  .messages-area:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: -2px;
  }

  .messages-list {
    display: flex;
    flex-direction: column;
    max-width: var(--max-content-width);
    margin: 0 auto;
  }

  .message-entry {
    scroll-margin-block: 6rem;
    border-radius: 8px;
    transition: background-color 0.25s ease;
    /* Этап 13.6: сообщения вне экрана и буфера не размечаются и не растеризуются (память
       iPhone: слои сотен код-блоков). После первого рендера браузер помнит настоящую
       высоту, 160px — оценка до него; скачки компенсирует actions/anchor-height.js. */
    content-visibility: auto;
    contain-intrinsic-size: auto 160px;
  }

  /* Цель поиска рендерится принудительно: прокрутка к ней центрирует по настоящей высоте. */
  .message-entry.is-forced-visible {
    content-visibility: visible;
  }

  .message-entry.highlighted {
    background: color-mix(in srgb, var(--accent-color) 16%, transparent);
  }

  .stage-window-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-sm);
    padding: 0.4rem 0.75rem;
    background: color-mix(in srgb, var(--accent-color) 12%, var(--bg-secondary));
    border-bottom: 1px solid var(--border-light);
    font-size: 0.85rem;
  }

  .stage-window-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stage-window-back,
  .stage-hint-btn,
  .stage-edit-save {
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    border: 1px solid var(--accent-color);
    background: var(--bg-primary);
    color: var(--accent-color);
    font-size: 0.85rem;
    cursor: pointer;
  }

  .stage-hint {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin: 0 0 var(--spacing-sm);
    padding: 0.5rem 0.75rem;
    border-radius: 10px;
    background: var(--bg-tertiary);
    font-size: 0.85rem;
    color: var(--text-secondary);
  }

  .stage-hint span {
    flex: 1 1 auto;
  }

  .stage-hint-btn.primary,
  .stage-edit-save {
    background: var(--accent-color);
    color: var(--bg-primary);
  }

  .include-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0 0 var(--spacing-sm);
  }

  .include-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    border: 1px solid var(--accent-color);
    color: var(--accent-color);
    font-size: 0.8rem;
  }

  .include-remove {
    position: relative;
    border: none;
    background: none;
    color: inherit;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }

  .stage-edit {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0 var(--spacing-md) var(--spacing-md);
  }

  .stage-edit-label {
    font-size: 0.8rem;
    color: var(--text-secondary);
  }

  .stage-edit input,
  .stage-edit textarea {
    width: 100%;
    box-sizing: border-box;
    font-size: 16px;
    padding: 0.5rem;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--bg-primary);
    color: var(--text-primary);
  }

  .stage-edit-hint {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin: 0;
  }

  .context-reset-marker {
    align-self: center;
    margin: 0.5rem auto 0.75rem;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    background: color-mix(in srgb, var(--accent-color) 12%, transparent);
    color: var(--accent-color);
    font-size: 0.75rem;
  }

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 1rem;
    color: var(--text-secondary);
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .spinner.small {
    width: 20px;
    height: 20px;
    border-width: 2px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-more {
    display: flex;
    justify-content: center;
    padding: var(--spacing-md);
  }

  .messages-shell {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .scroll-fab {
    position: absolute;
    right: 16px;
    bottom: 12px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid var(--border-color);
    background: var(--bg-elevated);
    color: var(--text-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--shadow-md);
    z-index: 10;
  }

  /* Этап 13: «Вверх» — стеком над «Вниз», место фиксированное (не прыгает, когда
     «Вниз» скрыта). При удержании — кольцо, заполняющееся за время long-press. */
  .scroll-fab--up {
    bottom: 64px;
    touch-action: none;
    -webkit-touch-callout: none;
    user-select: none;
    -webkit-user-select: none;
  }

  .scroll-fab--up::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid var(--accent-color);
    opacity: 0;
    transform: scale(0.7);
    pointer-events: none;
  }

  .scroll-fab--up.is-holding::after {
    animation: scroll-fab-hold 450ms linear forwards;
  }

  .scroll-fab.is-busy {
    opacity: 0.6;
  }

  @keyframes scroll-fab-hold {
    from {
      opacity: 0.25;
      transform: scale(0.7);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .scroll-fab--up.is-holding::after {
      animation: none;
      opacity: 1;
      transform: scale(1);
    }
  }

  .date-separator {
    align-self: center;
    margin: 0.5rem auto;
    padding: 0.2rem 0.75rem;
    border-radius: 999px;
    background: var(--bg-secondary);
    color: var(--text-muted);
    font-size: 0.75rem;
  }

  .suggestion-chips {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    align-items: center;
  }

  .suggestion-chip {
    padding: 0.7rem 1rem;
    border-radius: 12px;
    border: 1px solid var(--border-color);
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-size: 0.875rem;
  }

  .status-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem var(--spacing-lg);
    background: var(--bg-tertiary);
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  .status-bar.typing .status-indicator {
    background: var(--text-muted);
    animation: pulse 1.2s infinite;
  }

  .status-bar.error {
    background: var(--error-bg);
    color: var(--error-text);
  }

  .status-bar.generating {
    color: var(--success-color);
  }

  .status-indicator {
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 50%;
    animation: pulse 1.5s infinite;
  }

  .status-bar.error .status-indicator {
    background: var(--error-text);
    animation: none;
  }

  .status-bar.generating .status-indicator {
    background: var(--success-color);
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .input-area {
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--bg-secondary);
    border-top: 1px solid var(--border-color);
  }

  .input-row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
    max-width: var(--max-content-width);
    margin: 0 auto;
  }

  .attach-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    min-width: 44px;
    height: 44px;
    border-radius: 12px;
    border: 1px solid var(--border-color);
    background: var(--bg-tertiary);
    color: var(--text-primary);
    cursor: pointer;
    overflow: hidden;
  }

  .attach-btn input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    padding: 0;
  }

  .attach-btn span {
    font-size: 1.5rem;
    line-height: 1;
  }

  .attachment-preview-list {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--spacing-md);
    max-width: var(--max-content-width);
    margin: 0 auto var(--spacing-sm);
    padding: var(--spacing-sm);
    border: 1px solid var(--border-color);
    border-radius: 14px;
    background: var(--bg-secondary);
  }

  .attachment-preview {
    position: relative;
    width: 72px;
    height: 72px;
    flex: 0 0 auto;
  }

  .attachment-preview-image {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    object-fit: cover;
  }

  /* В11 (1.27.2): у мелких кнопок поверх картинок и внутри полей зона касания
     добрана невидимым ::after до 44px — вёрстка и вид не меняются. */
  .attachment-thumb-remove::after,
  .include-remove::after {
    content: '';
    position: absolute;
    inset: -8px;
  }

  .include-remove::after {
    inset: -12px;
  }

  .attachment-thumb-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 1rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .attachment-clear-btn {
    align-self: flex-start;
    border: none;
    background: none;
    color: var(--accent-hover);
    padding: 0;
  }

  .vision-model-hint {
    font-size: 0.8rem;
    color: var(--text-secondary, #888);
    margin-top: 0.25rem;
  }

  .composer-field {
    position: relative;
    flex: 1;
    min-width: 0;
  }

  .input-row textarea {
    display: block;
    width: 100%;
    resize: none;
    min-height: 44px;
    max-height: 150px;
    height: 44px;
    /* Слева — место под «+» внутри поля (и камеру в скан-чате), справа — под бейдж. */
    padding: 0.75rem 1rem 0.75rem 44px;
    border-radius: 22px;
    font-size: 1rem;
    line-height: 1.4;
    overflow-y: hidden;
  }

  .composer-field.with-camera textarea {
    padding-left: 80px;
  }

  .composer-field.with-badge textarea {
    padding-right: 44px;
  }

  /* Иконки внутри поля: прижаты к нижней строке, чтобы при многострочном
     тексте оставаться на уровне последней строки, как кнопка отправки. */
  .inline-btn {
    position: absolute;
    bottom: 6px;
    width: 32px;
    min-width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--text-secondary);
    padding: 0;
    cursor: pointer;
    overflow: hidden;
    z-index: 1;
  }

  .inline-attach {
    left: 6px;
  }

  .inline-camera {
    left: 42px;
  }

  .inline-btn span {
    font-size: 1.35rem;
    line-height: 1;
  }

  .assistant-badge {
    right: 6px;
    border: 2px solid transparent;
    transition: all var(--transition-fast);
  }

  .assistant-badge img {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    opacity: 0.55;
  }

  .assistant-badge.active {
    border-color: var(--accent-color);
    background: var(--accent-color);
  }

  .assistant-badge.active img {
    opacity: 1;
  }

  .send-btn {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    font-size: 1.25rem;
    transition: all var(--transition-fast);
    background: var(--accent-color);
    color: white;
  }

  .send-btn img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
  }

  .send-btn.to-assistant {
    box-shadow: 0 0 0 2px var(--bg-secondary), 0 0 0 4px var(--accent-color);
  }

  .send-btn:hover:not(:disabled) {
    background: var(--accent-hover);
  }

  .send-btn:disabled {
    background: var(--bg-tertiary);
    color: var(--text-muted);
  }

  /* «Повторить» у ошибки без ответа (1.27.0): главное действие строки ошибки. */
  .status-retry {
    margin-left: auto;
    flex-shrink: 0;
    min-width: 44px;
    min-height: 36px;
    padding: 0 0.9rem;
    border: 1px solid currentColor;
    border-radius: 18px;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
  }

  .stop-btn {
    margin-left: auto;
    /* В11 (1.27.2): 44px при прежней высоте строки статуса. */
    width: 44px;
    height: 44px;
    margin-top: -4px;
    margin-bottom: -4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: none;
    color: var(--error-text);
  }

  .archived-notice,
  .offline-notice {
    text-align: center;
    padding: 0.75rem;
    color: var(--text-muted);
    font-size: 0.875rem;
  }

  .error-notice {
    color: var(--error-text);
  }

  .travel-draft-notice {
    max-width: var(--max-content-width);
    margin: 0 auto var(--spacing-sm);
    padding: 0.55rem 0.75rem;
    border-radius: 8px;
    background: rgba(14, 165, 233, 0.1);
    color: #0284c7;
    font-size: 0.8125rem;
  }
</style>
